import { Component, OnInit, OnDestroy, ViewChild, Input } from '@angular/core';
import { Params, ActivatedRoute, Router } from '@angular/router';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { ChartConfiguration, ChartEvent, ChartType, Chart, registerables } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import annotationPlugin from 'chartjs-plugin-annotation';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';

import { AppConstants } from 'appshared/app-constants';
import * as Models from 'appshared/shared-models';
import { MiscTools } from 'appshared/misc-tools';
import { ValidationTools } from 'appshared/validation-tools';
import { TextTools } from 'appshared/text-tools';
import { SharedLicenseTools } from 'appshared/shared-license-tools';
import { LicenseValidationTools } from 'appshared/license-validation-tools';
import { UITools } from 'appshared/ui-tools';

import { LicensingService } from 'client/app/components/app-sections/licensing/licensing.service';
import { LicensingAdminService } from 'client/app/components/app-sections/licensing-admin/licensing-admin.service';
import { OrganizationsService } from 'client/app/components/app-sections/organizations/organizations.service';
import { UsersService } from 'client/app/components/app-sections/users/users.service';
import { AdminLogsService } from 'client/app/components/app-sections/admin-logs/admin-logs.service';
import { AppSettingsService } from 'client/app/components/app-sections/app-settings/app-settings.service';
import { ReportsService } from 'client/app/components/app-sections/reports/reports.service';
import { MyKeysService } from 'client/app/components/app-sections/my-keys/my-keys.service';
import { UiAlertsService } from 'client/app/components/ui-alerts/ui-alerts.service';
import { MarketplaceService } from 'client/app/components/app-sections/marketplace/marketplace.service';
import { MySettingsService } from 'client/app/components/app-sections/my-settings/my-settings.service';

import { TableLogsComponent } from 'client/app/components/shared/table-logs/table-logs.component';
import { TableJournalsComponent } from 'client/app/components/shared/table-journals/table-journals.component';
import { TableLicensesComponent } from 'client/app/components/shared/table-licenses/table-licenses.component';
import { TableMarketplaceLogsComponent } from 'client/app/components/shared/table-marketplace-logs/table-marketplace-logs.component';
import { KeyFeaturesComponent } from 'client/app/components/shared/key-features/key-features.component';
import { KeyMetersComponent } from 'client/app/components/shared/key-meters/key-meters.component';
import { PopupBoxComponent } from 'client/app/components/shared/popup-box/popup-box.component';

// Register core Chart.js components and the annotation plugin
Chart.register(...registerables, annotationPlugin);

@Component({
	selector: 'app-key-info',
	templateUrl: './key-info.component.html',
	styleUrls: ['./key-info.component.scss']
})
export class KeyInfoComponent implements OnInit, OnDestroy {
	@Input() staffMode: boolean = false; // determimes if certain things are shown in UI/code that's identical between staff and non-staff pages
	@Input() storageKeyPrefix: string = '';

	ac = AppConstants;
	tt = TextTools;
	slt = SharedLicenseTools;
	mt = MiscTools;

	@ViewChild('logsTable1') logsTable1: TableLogsComponent = null;
	@ViewChild('journalsTable1') journalsTable1: TableJournalsComponent = null;
	@ViewChild('allLicensesTable') allLicensesTable: TableLicensesComponent = null;
	@ViewChild('activeLicensesTable') activeLicensesTable: TableLicensesComponent = null;
	@ViewChild('searchLicensesTable') searchLicensesTable: TableLicensesComponent = null;
	@ViewChild(TableMarketplaceLogsComponent) marketPlaceLogsTable: TableMarketplaceLogsComponent = null;
	@ViewChild('keyFeatures') keyFeatures: KeyFeaturesComponent = null;
	@ViewChild('hostFeatures') hostFeatures: KeyFeaturesComponent = null;
	@ViewChild(KeyMetersComponent) keyMeters: KeyMetersComponent = null;
	@ViewChild(PopupBoxComponent) popupBox: PopupBoxComponent = null;

	// these are the main properties passed in to setup
	authUser: Models.AuthUser;
	pageMode: string = 'activation'; // or host or aggregate or ip
	activation: Models.LPActivation;
	userKey: Models.UserLicenseKey; // for shared code for non-staff mode
	hostid: string;
	ip: string;
	tabID: string = '';
	filterID: number = 0;

	// *********************************************************
	setupComplete: boolean = false;

	loading: boolean = true;
	saving: boolean = false;
	actionsReady: boolean = false;

	firstLoad: boolean = true;

	// *********************************************************
	// non-staff mode stuff
	registeredKey = false;
	orgUsers: Models.User[] = [];

	showAdminButton = false;
	showPurge = false;
	showSpecial = false;

	selfServicePackageId: number = 0;
	selfServiceTemplateId: number = 0;
	showSelfServiceExtendButton: boolean = false;
	showSelfServiceCreateButton: boolean = false;

	sharedWithUserIds: number[] = [];
	sharedWithUsers: Models.User[] = [];

	hostids: Models.UserHostId[] = [];
	hostidLabels: {} = {};
	label: string = '';

	meterTypes: string[] = [];

	editKeyLabelErrors: string[] = [];
	editKeyLabelForm: UntypedFormGroup;

	addHostIdLabelsErrors: string[] = [];
	addHostIdLabelsForm: UntypedFormGroup;

	editHostIdLabelErrors: string[] = [];
	editHostIdLabelForm: UntypedFormGroup;

	snoozeErrors: string[] = [];
	snoozeForm: UntypedFormGroup;
	snoozeId: number;
	snoozeType: string;
	theSnooze: Models.UserLicenseKeySnooze = null;

	// *********************************************************
	// MODE: activation
	// *********************************************************
	organization: Models.Organization;
	organizationForUsers: Models.Organization;

	allTemplates: Models.KeyTemplate[] = []; // all of them
	template: Models.KeyTemplate; // template for the key
	otherTemplates: Models.KeyTemplate[] = []; // other templates the key could be changed to

	expiryMode = '';

	canManageKeyViaTemplate: boolean = false; // full blown access/template based able to edit key
	canManageKey: boolean = false; // either write access via template or specific write access

	// canSetOrganization: boolean = false; // old way of linking to Orgs
	canSetTemplate: boolean = false; // limited
	canAddJournals: boolean = false;
	canSetWriteAccess: boolean = false;
	canManageCommercial: boolean = false;
	canRunPenTest: boolean = false;
	canConfigureMarketplace = false;

	showOfflineButton: boolean = false; // special for offline keys
	showDownloadRecentButton: boolean = false;

	showMeters: boolean = false;
	showMetersTab: boolean = false;
	metersTabLabel: string = 'Meters';

	canShowMeterProtocolEquiv: boolean = false;
	showMeterProtocolEquiv: boolean = false;
	showBrandFilters: boolean = false;

	userKeys: Models.UserLicenseKey[] = [];
	otherOrgUsers: Models.User[] = [];

	accountOwner: Models.User = null;
	accountSE: Models.User = null;
	accountTAM: Models.User = null;
	accountSA: Models.User = null;
	adminLogs: Models.AdminLog[] = [];
	journals: Models.JournalEntry[] = [];

	lpUser = '';
	addedByUser: Models.User = null;
	editedByUser: Models.User = null;
	updatedDateToShow: Date = null;

	writeAccessUsers: Models.User[] = [];

	searching: boolean = false;
	showSearchResults: boolean = false;
	searchForm: UntypedFormGroup;
	searchResults: Models.LPLicense[] = [];
	hostidFilter = '';

	activeLicenses: Models.LPLicense[] = [];

	keyWarnings = [];
	meterWarnings = [];
	nonEnforcedWarnings = [];

	recentUsedHosts: string[] = [];
	recentHostsPopup = 'Number of Activations (host IDs) reporting non-zero traffic (either via meter or protocol) over the last '
		+ AppConstants.recentUsedHostsDays + ' days';
	recentHostIconPopup = 'Reporting non-zero traffic over the last ' + AppConstants.recentUsedHostsDays + ' days';
	offlineHostIconPopup = 'Might be offline or unable to communicate with license server.  Hasn\'t reported in last day, but did report in within the last ' + AppConstants.hostOfflineMaxDays + ' days.';
	upgradeIconPopup = 'Reporting in over the last ' + AppConstants.recentUsedHostsDays + ' days via meter(s), but not protocols.  Protocol reporting is blocked or may need upgrade to v13 or later.';

	showFirstDates: boolean = false;

	showMaxHostsExtra: boolean = false;
	fullLicenseCount: number = 0;

	salesforceAccountInfoBlock = '';
	salesforceOpportunityInfoBlock = '';
	sfFetching: boolean = false;
	salesForceUrl = '';

	orgSearching: boolean = false;

	keyProduct: Models.LicenseProduct;
	keyProductProperties: Models.LicenseProductProperty[];

	showOrgLinkButton: boolean = true;
	orgLinkMatches = [];
	showNoMatches: boolean = false;

	keyExpiryCalc: Date;
	licExpiryCalc: Date;

	hostIdCounts: any = {};
	licenseCounters: any = {};
	licenseMeterInitials: any = {};
	licenseMeterNames: any = {};
	licenseFormats: any = {};

	meterLabelsByType: any = {};
	anyMeterLabels: boolean = false;

	bcSummaryByHostId: any[] = [];

	checkingCompliance: boolean = false;
	isCompliant: boolean = true;
	// templateComplianceIssues: string[] = null;

	commercialTypeInfo: any = null;
	commercialGuesses: any = null;

	usageSummaryForm: UntypedFormGroup;
	usageSummaryDataTypeSelections: any[] = [];

	keySnoozes: Models.UserLicenseKeySnooze[] = [];
	userSnoozes = {};

	marketplaceLabel: string = '';
	marketplaceAccountIdentifier: string = '';
	marketplaceProducts: string[] = [];
	marketPlaceAccountIdLabel: string = 'ID';
	lastMarketplaceUsageReported: Date = null;
	marketplaceLogs: Models.MarketplaceUsageReport[] = [];
	productCache: any = {};
	selectedMarketPlaceProtocolSet: Models.ProtocolSet = null;

	canReportToMarketplace: boolean = false;
	manualMarketplaceForm: UntypedFormGroup;

	prevId: number = -1;
	nextId: number = -1;
	prevNote: string = '';
	nextNote: string = '';

	// *********************************************************
	// MODE: hostid/ip
	// *********************************************************

	licenses: Models.LPLicense[] = [];

	keys: Models.LPActivation[] = [];

	hostKeys: any = {};

	activationIDsForObject: number[] = [];
	activationIDsWithUsage: number[] = [];

	filterKey: string = '';
	otherKeyCount: number = 0;

	latestID: number = 0;
	showLinkOfflineLicense = false;
	linkOfflineForm: UntypedFormGroup;

	meterTypesPerActivation: any = {};
	expiryModePerActivation: any = {};
	expiryExtraActivation: any = {};

	// version history...
	historyLoading: boolean = false;
	versionHistory: Models.LPVersionHistory[] = [];

	keyProducts: Models.LicenseProduct[] = [];

	showSelectedKeyLicFeatures = false;

	showFileFootnote = false;

	labels: Models.UserHostId[] = [];

	allOrgs: Models.Organization[] = [];
	staffUsers: Models.User[] = [];

	// *********************************************************
	// MODE: aggregate
	// *********************************************************

	aggregateForm: UntypedFormGroup;
	keysAndHostIds: string = '';
	typeOfAggregateQuery: string = '';
	typesOfQueries: any[] = [
		{ value: 'keys', label: 'List of Keys' },
		// { value: 'hostids', label: 'List of Host IDs' },
		{ value: 'mix', label: 'List of Keys & Host IDs (separated by a colon)' }
	];

	resolving: boolean = false;
	keysAndHostIdsCombos: any[] = [];
	activationIdAndHostIdsCombos: any[] = [];
	resolveProblems: string[] = [];
	resolveData: any = {};

	aggregateSelectionMode: boolean = true;
	aggregateSelectionForm: UntypedFormGroup;

	// *********************************************************
	// common usage stuff
	// *********************************************************

	rateNumFmt = '1.1-1';
	percFmt = '1.1-1';

	showNoUseDays: boolean = true;
	showRawData: boolean = false;

	timeModes: any[] = [
		{ value: 'month', label: 'Month' },
		{ value: 'quarter', label: 'Quarter' },
		{ value: 'year', label: 'Year' }
	];
	timeMode: string = 'month';
	timeModeLabel: string = 'Month';

	showSetsButton: boolean = true;

	// all defined sets
	availableSets: Models.ProtocolSet[] = [];

	// combined linked + session sets
	selectedProtocolSets: Models.ProtocolSet[] = [];

	// phantom sets for doing meter equiv.
	phantomSets: Models.ProtocolSet[] = [];
	phantomSetIds: any = {};

	// just session sets
	supportsSessionSet: boolean = true;

	sessionSetIdentifier: string = '';
	sessionProtocolSets: Models.ProtocolSet[] = [];

	// just linked sets
	supportsLinkedSet: boolean = false;
	linkedProtocolSets: Models.ProtocolSet[] = [];

	protocolSetsPosition: string = 'top';
	canLinkProtocolSets: boolean = false;

	penTestRunning: boolean = false;

	allPenTests: Models.BroadcasterPenTest[] = [];
	failedPenTests: Models.BroadcasterPenTest[] = [];
	failedPenTestIPs: string[] = [];

	// penTestsById: any = {};
	// penTestCodeById: any = {};

	protocolShowAllMonths: boolean = false;

	// *********************************************************
	// meter usage for key
	showMeterTraffic = false;
	byDayMeterBuffer = 200;
	byMonthMeterBuffer = 200;

	monthlyMeterUsage: Models.LPMeterReportByMonth[] = [];
	chunkedMeterUsage: Models.LPMeterReportByMonth[] = [];
	meterProductsToShow: string[] = [];

	byDayMeterLoading = false;
	byDayMeterProduct = '';
	byDayMeterMonth = 0;
	byDayMeterYear = 0;
	byDayMeterUsage: Models.LPMeterReportByDay[] = [];
	byDayMeterTotal = 0;
	byDayMeterAvgAbsChg = 0;

	showMeterTypeExtra = false;

	showOverageCounts: any = {};
	monthlyProjectedAmounts: any = {};

	// *********************************************************
	// protocol (data) usage for key
	rotateAxis: boolean = true;
	protocolReady: boolean = false;

	showProtocolTraffic = false;
	byDayProtocolBuffer = 210;
	byMonthProtocolBuffer = 210;

	monthlyProtocolUsage: Models.LPMeterReportByMonth[] = [];
	chunkedProtocolUsage: Models.LPMeterReportByMonth[] = [];
	protocolProducts: string[] = [];
	protocolProductsToShow: string[] = [];
	protocolProductsVisible: string[] = [];
	protocolsInfo: any[] = [];

	onlyShowTotals: boolean = false;
	onlyShowTotalsStorageKey: string = 'activationView.onlyShowTotals';

	protocolTimeChunks: any[] = [];
	protocolTimeChunksToShow: any[] = [];

	byDayProtocolLoading: boolean = false;
	byDayProtocolProduct = '';
	byDayProtocolMonth = 0;
	byDayProtocolYear = 0;
	byDayProtocolUsage: Models.LPMeterReportByDay[] = [];
	byDayProtocolTotal = 0;
	byDayProtocolAvgAbsChg = 0;

	hasIncomingProtocolData: boolean = false;
	hasTranscodeProtocolData: boolean = false;
	hasOutgoingProtocolData: boolean = false;

	// *********************************************************
	// billing code related
	billingCodesByCode: any = {};

	billingCodeDisplay: string = 'code'; // or label

	// 0 - means show all usage together, -1 - means show no-code usage, # - show usage for that b-code
	usedProtocolBillingCodes: Models.BillingCode[] = [];
	usedProtocolBillingCodesToShow: Models.BillingCode[] = [];
	activeProtocolBillingCode: number = -1;
	activeProtocolBillingCodeLabel = 'All Usage Data';
	activeProtocolBillingCodeShortLabel = '';
	protocolBillingCodeToSearch: string = '';

	invalidBillingCodeEntries: Models.LPInvalidBillingCodeCache[] = [];

	// *********************************************************
	// device types + brands

	uniqueProtocolDeviceTypes: number[] = [];
	uniqueProtocolBrands: string[] = [];
	uniqueProtocolDeviceTypesAndBrands: any[] = [];

	brandFilter: string = AppConstants.brandFilterNoFilter;

	// *********************************************************
	// charting
	public maxMonthsForCharts: number = 12;
	public newToOld: boolean = true;
	public includeCurrentMonth: boolean = false;

	public lineChartType: ChartType = 'line';
	public lineColors: string[] = ['green', 'blue', 'red'];

	// @ViewChild(BaseChartDirective) chart?: BaseChartDirective;
	@ViewChild('meterChart') meterChart: BaseChartDirective = null;

	public showMeterChart: boolean = true;
	public meterChartLabel: string = '';
	public meterChartData: ChartConfiguration['data'] = {
		datasets: [],
		labels: [],
	};
	public meterChartOptions: ChartConfiguration['options'] = {
		elements: {},
		scales: {
			y: {
				position: 'left',
				ticks: {
					callback: function (value, index, values) {
						return TextTools.formattedMB(+value).replace('.0', '');
					}
				}
			}
		},
		plugins: {
			legend: { display: true },
			tooltip: {
				callbacks: {
					label: function (context) {
						return TextTools.formattedMB(+context.parsed.y).replace('.0', '');
					}
				}
			},
			annotation: {}
		}
	};

	@ViewChild('protocolChart') protocolChart: BaseChartDirective = null;
	public showProtocolChart: boolean = false;
	public protocolChartLabel: string = '';
	public protocolChartProduct: string = '';
	public protocolChartData: ChartConfiguration['data'] = {
		datasets: [],
		labels: [],
	};
	public protocolChartOptions: ChartConfiguration['options'] = {
		elements: {},
		scales: {
			y: {
				position: 'left',
				ticks: {
					callback: function (value, index, values) {
						return TextTools.formattedMB(+value);
					}
				}
			},
		},
		plugins: {
			legend: { display: false },
			tooltip: {
				callbacks: {
					label: function (context) {
						return TextTools.formattedMB(+context.parsed.y).replace('.0', '');
					}
				}
			},
			annotation: {}
		}
	};

	// *********************************************************
	// new stuff when there are lots of billing codes...
	lotsOfBillingCodes: boolean = false;
	billingCodeFilter: string = '';
	bcFilterChangeTimeOut: any = null;
	bcTimeoutMS: number = 500;

	licenseTableOptions: any = {};

	// *********************************************************
	constructor(
		private route: ActivatedRoute,
		private router: Router,
		private uiAlertsService: UiAlertsService,
		private licensingAdminService: LicensingAdminService,
		private licensingService: LicensingService,
		private myKeysService: MyKeysService,
		private mySettingsService: MySettingsService,
		private organizationsService: OrganizationsService,
		private usersService: UsersService,
		private adminLogsService: AdminLogsService,
		private reportsService: ReportsService,
		private marketplaceService: MarketplaceService,
		private appSettingsService: AppSettingsService) {
	}

	// *********************************************************
	ngOnInit(): void {
		// console.log('in ngOnInit pm=' + this.pageMode);
	}

	// *********************************************************
	ngOnDestroy(): void {
		// console.log('in ngOnDestroy pm=' + this.pageMode);

		this.uiAlertsService.clearMsgByCode('compliance');
	}

	// *********************************************************
	async setupComponent(authUser: Models.AuthUser, pageMode: string, activation: Models.LPActivation, userKey: Models.UserLicenseKey, hostid: string, ip: string, tabID: string, filterID: number) {
		this.setupComplete = false;

		this.authUser = authUser;
		this.pageMode = pageMode;
		this.activation = activation;
		this.userKey = userKey;
		this.hostid = hostid;
		this.ip = ip;
		this.tabID = tabID;
		this.filterID = filterID;

		if (!['activation', 'hostid', 'ip', 'aggregate'].includes(this.pageMode)) {
			this.uiAlertsService.addMsg('"' + this.pageMode + '" is not a supported page mode', 'danger', '', false, 0);
			this.router.navigate([AppConstants.urls.notfound]);
			return;
		} // if

		if (!this.staffMode && ['ip', 'aggregate'].includes(this.pageMode)) {
			this.uiAlertsService.addMsg('"' + this.pageMode + '" is not a supported in non-staff mode', 'danger', '', false, 0);
			this.router.navigate([AppConstants.urls.notfound]);
			return;
		} // if

		if (this.staffMode) {
			this.canAddJournals = ValidationTools.checkAccess(this.authUser, 'add-journals');
			this.canLinkProtocolSets = ValidationTools.checkAccess(this.authUser, 'link-protocol-sets-to-keys');
			this.canRunPenTest = ValidationTools.checkAccess(this.authUser, 'manage-keys');
			this.canShowMeterProtocolEquiv = ValidationTools.checkAccess(this.authUser, 'compare-meters-to-protocol');
			this.showBrandFilters = ValidationTools.checkAccess(this.authUser, 'expose-brands');;
		} // if

		this.showAdminButton = !this.staffMode && this.authUser && ValidationTools.checkRole(this.authUser.role, AppConstants.staffUserRole);

		let settings: any = {};
		try {
			if (localStorage.getItem(this.storageKeyPrefix + '.settings'))
				settings = JSON.parse(localStorage.getItem(this.storageKeyPrefix + '.settings'));
		} catch (e: any) { }
		const settingKeys: string[] = Object.keys(settings);

		if (settingKeys.includes('rotateAxis')) this.rotateAxis = settings.rotateAxis;
		if (settingKeys.includes('showRawData')) this.showRawData = settings.showRawData;
		if (settingKeys.includes('protocolSetsPosition')) this.protocolSetsPosition = settings.protocolSetsPosition;
		if (settingKeys.includes('billingCodeDisplay')) this.billingCodeDisplay = settings.billingCodeDisplay;
		if (settingKeys.includes('onlyShowTotals')) this.onlyShowTotals = settings.onlyShowTotals;
		if (settingKeys.includes('showNoUseDays')) this.showNoUseDays = settings.showNoUseDays;
		if (settingKeys.includes('showMeterProtocolEquiv')) this.showMeterProtocolEquiv = settings.showMeterProtocolEquiv;
		if (settingKeys.includes('newToOld')) this.newToOld = settings.newToOld;
		if (settingKeys.includes('includeCurrentMonth')) this.includeCurrentMonth = settings.includeCurrentMonth;
		this.setupComplete = true;
		this.loadData();
	} // 

	// *********************************************************
	async loadData(forceRefresh: boolean = false) {
		try {
			this.loading = true;
			this.actionsReady = false;

			const now = new Date();

			this.licenseTableOptions = { staffMode: this.staffMode, showMeters: this.showMeters, protocolReady: this.protocolReady, key: this.activation ? this.activation.key : '' };

			if (this.allLicensesTable) this.allLicensesTable.updateContent([], 'WIPE-FILTER.' + this.storageKeyPrefix + '-all-licenses', this.licenseTableOptions);
			if (this.activeLicensesTable) this.activeLicensesTable.updateContent([], 'WIPE-FILTER.' + this.storageKeyPrefix + '-active-licenses', this.licenseTableOptions);
			if (this.searchLicensesTable) this.searchLicensesTable.updateContent([], 'WIPE-FILTER.' + this.storageKeyPrefix + '-search-licenses', this.licenseTableOptions);

			this.resetMeterUsage();
			this.resetProtocolUsage();

			this.registeredKey = (!this.staffMode && this.userKey && this.userKey.id !== 0);

			this.availableSets = await this.licensingAdminService.getProtocolSets();
			if (!this.staffMode) {
				// this should be done on the back-end for true non-staff users
				// however, do it here for when a staff member is looking at things
				// in user mode..
				for (const item of this.availableSets) {
					item.name = 'Protocol Set (' + item.protocolsArr.length + ' ' + (item.protocolsArr.length === 1 ? 'protocol' : 'protocols') + ')';
					item.description = '';
					item.added_by = 0;
					item.edited_by = 0;
					item.added_on = null;
					item.edited_on = null;
					item.num_keys = 0;
				} // for
			}


			this.phantomSets = [];
			this.linkedProtocolSets = [];
			this.sessionProtocolSets = [];

			if (this.staffMode) {
				for (const pSet of this.availableSets) {
					if (pSet.name === AppConstants.protectedProtocolSetName) {
						this.phantomSets.push(pSet);
						this.phantomSetIds['protected_mb'] = pSet.id;
					} else if (pSet.name === AppConstants.allOutputsProtocolSetName) {
						this.phantomSets.push(pSet);
						this.phantomSetIds['output_mb'] = pSet.id;
						this.phantomSetIds['output_mb_meter'] = pSet.id;
					} // if
				} // for

				if (this.phantomSets.length !== 2) this.canShowMeterProtocolEquiv = false;

				if (this.pageMode === 'activation')
					this.sessionSetIdentifier = this.activation.id.toString();
				else if (this.pageMode === 'hostid')
					this.sessionSetIdentifier = this.hostid;
				else if (this.pageMode === 'aggregate')
					this.sessionSetIdentifier = 'aggregate';

				if (this.pageMode === 'activation') {
					const linkedSets: Models.ActivationProtocolSet[] = await this.licensingService.getActivationProtocolSets(this.activation.id);
					for (const linkedSet of linkedSets) {
						const idx = MiscTools.findIndex(this.availableSets, linkedSet.set_id);
						if (idx !== -1) {
							if (!isNaN(linkedSet.projected)) this.availableSets[idx]['projected'] = linkedSet.projected;
							if (!isNaN(linkedSet.current_usage)) this.availableSets[idx]['current_usage'] = linkedSet.current_usage;
							this.availableSets[idx]['exclude_billing_code'] = linkedSet.exclude_billing_code;

							if (linkedSet.data_type === 'protocol-data') {
								const copyOfSet: Models.ProtocolSet = MiscTools.deepClone(this.availableSets[idx]);
								if (linkedSet.exclude_billing_code)
									copyOfSet['__exclude_billing_code'] = linkedSet.exclude_billing_code;
								this.linkedProtocolSets.push(copyOfSet);
							} // if
						} // if
					} // for
					this.supportsLinkedSet = true;
				} // if

				if (localStorage.getItem('CLEAN.' + this.storageKeyPrefix + 'sessionProtocolSets.' + this.sessionSetIdentifier) != null) {
					const tmpIds: number[] = JSON.parse(localStorage.getItem('CLEAN.' + this.storageKeyPrefix + 'sessionProtocolSets.' + this.sessionSetIdentifier));
					for (const setId of tmpIds) {
						const idx = MiscTools.findIndex(this.availableSets, setId);
						if (idx !== -1) this.sessionProtocolSets.push(this.availableSets[idx]);
					} // for
				} // if

				this.selectedProtocolSets = this.linkedProtocolSets.concat(this.sessionProtocolSets);
			} else {
				this.supportsSessionSet = false;
				this.showSetsButton = false;

				if (this.pageMode === 'activation' || this.pageMode === 'hostid') {
					this.sessionSetIdentifier = this.userKey.activation_id.toString();

					// uncomment the following block to enable protocol sets for non-staff
					const linkedSets: Models.ActivationProtocolSet[] = await this.myKeysService.getKeysProtocolSets(this.activation.key);
					for (const linkedSet of linkedSets) {
						const idx = MiscTools.findIndex(this.availableSets, linkedSet.set_id);
						if (idx !== -1) {
							if (!isNaN(linkedSet.projected)) this.availableSets[idx]['projected'] = linkedSet.projected;
							if (!isNaN(linkedSet.current_usage)) this.availableSets[idx]['current_usage'] = linkedSet.current_usage;
							this.availableSets[idx]['exclude_billing_code'] = linkedSet.exclude_billing_code;
							if (linkedSet.data_type === 'protocol-data') {
								const copyOfSet: Models.ProtocolSet = MiscTools.deepClone(this.availableSets[idx]);
								if (linkedSet.exclude_billing_code)
									copyOfSet['__exclude_billing_code'] = linkedSet.exclude_billing_code;
								this.linkedProtocolSets.push(copyOfSet);
							} // if
						} // if
					} // for
					this.supportsLinkedSet = true;
					this.showSetsButton = linkedSets.length > 0;
				} // if

				this.selectedProtocolSets = this.linkedProtocolSets;
			} // if

			this.keyProducts = await this.licensingAdminService.getProducts(forceRefresh);

			if (this.staffMode) {
				this.allOrgs = this.organizationsService.getAll();
				this.staffUsers = this.usersService.getForRole('staff');
				this.canConfigureMarketplace = false;
				this.canReportToMarketplace = false;
			} else {
				this.hostids = await this.myKeysService.getUserHostids(forceRefresh);
				this.hostidLabels = {};
				for (const hi of this.hostids)
					this.hostidLabels[hi.hostid] = hi.label;
			} // if

			if (this.pageMode === 'activation') {
				// is this a package key, is it out of activations and can it be extended
				if (!this.staffMode) {
					await this.workoutSelfService();
					this.showPurge = (this.userKey.is_deleted === 1 && this.userKey.add_method !== 'zcp-package-generated' && this.userKey.add_method !== 'wix');
				} // if

				// console.log('in load AA pm=' + this.pageMode);

				this.prevId = -1;
				this.nextId = -1;

				if (this.staffMode) {
					let lastSearchIds: number[] = [];
					if (localStorage.getItem('CLEAN.' + this.storageKeyPrefix + 'licensingSearch.latestIDs'))
						lastSearchIds = JSON.parse(localStorage.getItem('CLEAN.' + this.storageKeyPrefix + 'licensingSearch.latestIDs'));
					if (lastSearchIds && lastSearchIds.length > 1) {
						const idx = lastSearchIds.indexOf(this.activation.id);
						if (idx !== -1) {
							if (idx > 0)
								this.prevId = lastSearchIds[idx - 1];
							else
								this.prevId = lastSearchIds[lastSearchIds.length - 1];

							if (idx === lastSearchIds.length - 1)
								this.nextId = lastSearchIds[0];
							else
								this.nextId = lastSearchIds[idx + 1];

							this.prevNote = 'Based on last key search (sorted newest to oldest), go to previous key.';
							this.nextNote = 'Based on last key search (sorted newest to oldest), go to next key.';

							this.prevNote += ' Current key is # ' + (idx + 1) + ' of ' + lastSearchIds.length;
							this.nextNote += ' Current key is # ' + (idx + 1) + ' of ' + lastSearchIds.length;
						} // if
					} // if

					this.salesForceUrl = await this.appSettingsService.getSalesForceUrl();
				} else if (this.registeredKey) {
					this.keySnoozes = [];
					const userSnoozes = await this.myKeysService.getUserSnoozes();
					for (const snooze of userSnoozes)
						if (snooze.user_key_id === this.userKey.id)
							this.keySnoozes.push(snooze);

					this.keySnoozes.sort((a, b) => (a.expires_at > b.expires_at) ? 1 : -1);

					const allUsersKeys = await this.myKeysService.getUserKeys(false, forceRefresh);

					const activeKeyIds: number[] = [];
					const inactiveKeyIds: number[] = [];
					for (const key of allUsersKeys) {
						const exp = SharedLicenseTools.getKeyExpiration(key.activation, '', true);
						if ((key.activation.enabled === 0 && !SharedLicenseTools.isSpecialKey(key.activation)) || (exp != null && MiscTools.hasExpired(exp)))
							activeKeyIds.push(key.id);
						else
							inactiveKeyIds.push(key.id);
					} // for

					const allKeyIds: number[] = activeKeyIds.concat(inactiveKeyIds);
					if (allKeyIds && allKeyIds.length > 1) {
						const idx = allKeyIds.indexOf(this.userKey.id);
						if (idx !== -1) {
							if (idx > 0)
								this.prevId = allKeyIds[idx - 1];
							else
								this.prevId = allKeyIds[allKeyIds.length - 1];

							if (idx === allKeyIds.length - 1)
								this.nextId = allKeyIds[0];
							else
								this.nextId = allKeyIds[idx + 1];

							this.prevNote = 'Go to your previous key.';
							this.nextNote = 'Go to your next key.';

							this.prevNote += ' Current key is # ' + (idx + 1) + ' of ' + allKeyIds.length;
							this.nextNote += ' Current key is # ' + (idx + 1) + ' of ' + allKeyIds.length;
						} // if
					} // if
				} // if

				// if there's a tabID and the tab is visible, click it
				this.clickTab();

				this.updatedDateToShow = this.activation.updated_at;
				if (this.activation.zcp_updated_at != null) this.updatedDateToShow = this.activation.zcp_updated_at;

				this.activation.meters.sort(SharedLicenseTools.meterSort);
				this.keyExpiryCalc = SharedLicenseTools.getKeyExpiration(this.activation);
				this.licExpiryCalc = SharedLicenseTools.getLicenseExpiration(this.activation);

				this.commercialTypeInfo = null;
				if (this.staffMode) {
					if (this.activation.commercial_type && this.activation.commercial_type !== '') {
						const cIdx = MiscTools.findIndexGeneric(AppConstants.keyCommercialTypes, 'value', this.activation.commercial_type);
						if (cIdx !== -1) {
							this.commercialTypeInfo = MiscTools.deepClone(AppConstants.keyCommercialTypes[cIdx]);
							this.commercialTypeInfo.label = (cIdx + 1) + '. ' + this.commercialTypeInfo.label;
						} // if
					} // if
					this.commercialGuesses = LicenseValidationTools.workOutCommercialType(this.activation);

					this.allTemplates = await this.licensingAdminService.getTemplates();
					this.otherTemplates = [];
					this.template = null;
					if (this.activation.zcp_template_id && this.activation.zcp_template_id !== 0) {
						const idx = MiscTools.findIndex(this.allTemplates, this.activation.zcp_template_id);
						if (idx !== -1) this.template = this.allTemplates[idx];
					} // if
					for (const template of this.allTemplates)
						if (template.product === this.activation.product
							&& (!this.activation.zcp_template_id || this.activation.zcp_template_id === 0 || template.id !== this.activation.zcp_template_id))
							this.otherTemplates.push(template);

					const userTemplateIDs = await this.licensingAdminService.getUserTemplateIDs();
					const writeAccessArr: Models.KeyWriteAccess[] = await this.licensingService.getKeyWriteAccess(this.activation.id);

					this.writeAccessUsers = [];
					for (const writeAccess of writeAccessArr) {
						const u = this.usersService.getOne(writeAccess.user_id);
						if (u) this.writeAccessUsers.push(u);
					} // for

					// let's figure out access.....
					this.canManageKeyViaTemplate = ValidationTools.checkAccess(this.authUser, 'manage-keys')
						&& userTemplateIDs && this.template && userTemplateIDs.includes(this.template.id);

					if (this.canManageKeyViaTemplate) {
						this.canManageKey = true;
						this.canSetWriteAccess = ValidationTools.checkAccess(this.authUser, 'set-key-write-access');
					} else {
						const idx = MiscTools.findIndexGeneric(writeAccessArr, 'user_id', this.authUser.id);
						this.canManageKey = ValidationTools.checkAccess(this.authUser, 'manage-keys') && idx !== -1;
					} // if
				} // if

				this.actionsReady = true;

				this.canManageCommercial = this.staffMode && ValidationTools.checkAccess(this.authUser, 'manage-commercial');
				this.canSetTemplate = this.staffMode && !this.template && ValidationTools.checkAccess(this.authUser, 'change-key-template')
					&& !this.activation.type.startsWith(AppConstants.offlinePrefix)
					&& !SharedLicenseTools.isSpecialKey(this.activation);

				// if there's a tabID and the tab is visible, click it
				this.clickTab();

				const now = new Date();

				this.showOfflineButton = this.canManageKey && this.activation.type.startsWith(AppConstants.offlinePrefix)
					&& this.activation.count < this.activation.max
					&& (this.keyExpiryCalc == null || this.keyExpiryCalc.getTime() > now.getTime());

				this.expiryMode = SharedLicenseTools.getExpiryMode(this.activation);

				if (this.expiryMode === 'meter') {
					this.showOverageCounts = {};
					for (const product of AppConstants.meterProducts) {
						this.showOverageCounts[product] = 0;
					} // for

					for (const meter of this.activation.meters) {
						if (meter.enabled === 1 && meter.resets === 'monthly' && +meter.projected > 0)
							this.showOverageCounts[meter.product]++;

						let starts = new Date(meter.created_at);
						if (meter.starts_at) starts = new Date(meter.starts_at);
						const exp = new Date(meter.expires_at);
						if (meter.enabled === 1 && exp.getTime() > now.getTime() && starts.getTime() <= now.getTime() && meter.label && meter.label !== '') {
							this.anyMeterLabels = true;
							if (!this.meterLabelsByType[meter.product]) this.meterLabelsByType[meter.product] = [];
							if (!this.meterLabelsByType[meter.product].includes(meter.label)) this.meterLabelsByType[meter.product].push(meter.label);
						} // if
					} // for
				} // if

				if (!this.activation.parsedParameters)
					this.activation.parsedParameters = SharedLicenseTools.parseRubyHash(this.activation.parameters);

				this.keyProduct = await this.licensingAdminService.getProductByName(this.activation.product);
				if (this.keyProduct) {
					this.keyProductProperties = await this.licensingAdminService.getProductProperties(false, this.keyProduct.id);

					this.showMeters = this.keyProduct.show_meters && (this.staffMode ? this.keyProduct.show_meters > 0 : this.keyProduct.show_meters === 2);
					this.showMetersTab = (this.linkedProtocolSets.length > 0 || this.showMeters);
					if (this.linkedProtocolSets.length > 0)
						if (this.showMeters && this.activation.meters.length > 0)
							this.metersTabLabel = 'Meters+Sets';
						else
							this.metersTabLabel = 'Sets';

					this.protocolReady = (this.staffMode ? this.keyProduct.show_protocols > 0 : this.keyProduct.show_protocols === 2);

					this.canConfigureMarketplace = ValidationTools.checkAccess(this.authUser, 'configure-key-for-marketplace')
						&& this.keyProduct.name.startsWith('broadcaster')
						&& this.keyProduct.fulfillment_product != null
						&& this.keyProduct.fulfillment_product.startsWith('broadcaster');

					// was in user key view, but doesn't seem to be used anymore
					// this.showLastVersion = this.keyProduct.show_protocols && this.keyProduct.show_protocols > 0;
				} // if

				if (this.keyMeters)
					this.keyMeters.updateContent(this.activation, this.linkedProtocolSets, [], { staffMode: this.staffMode });

				await this.workoutHostIDProperties();

				await MiscTools.delay(50);
				if (this.keyFeatures)
					this.keyFeatures.updateContent(this.activation.parsedParameters, this.activation.product, { staffMode: this.staffMode, hostMode: false });

				this.licenseTableOptions = { staffMode: this.staffMode, showMeters: this.showMeters, protocolReady: this.protocolReady, key: this.activation ? this.activation.key : '' };
				if (this.allLicensesTable) this.allLicensesTable.updateContent(this.activation.licenses, 'WIPE-FILTER.' + this.storageKeyPrefix + '-all-licenses', this.licenseTableOptions);
				if (this.activeLicensesTable) this.activeLicensesTable.updateContent(this.activeLicenses, 'WIPE-FILTER.' + this.storageKeyPrefix + '-active-licenses', this.licenseTableOptions);

				this.organization = null;
				this.accountOwner = null;
				this.accountSE = null;
				this.accountTAM = null;
				this.accountSA = null;
				if (this.staffMode) {
					// let canRegisterUserKeys = false;
					if (this.activation.zcp_org_id && this.activation.zcp_org_id !== 0) {
						this.organization = this.organizationsService.getOne(this.activation.zcp_org_id);
						if (this.organization && this.organization.salesforce_account_owner_id && this.organization.salesforce_account_owner_id !== 0)
							this.accountOwner = this.usersService.getOne(this.organization.salesforce_account_owner_id);

						if (this.organization && this.organization.salesforce_se_id && this.organization.salesforce_se_id !== 0)
							this.accountSE = this.usersService.getOne(this.organization.salesforce_se_id);

						if (this.organization && this.organization.salesforce_tam_id && this.organization.salesforce_tam_id !== 0)
							this.accountTAM = this.usersService.getOne(this.organization.salesforce_tam_id);

						if (this.organization && this.organization.salesforce_sa_id && this.organization.salesforce_sa_id !== 0)
							this.accountSA = this.usersService.getOne(this.organization.salesforce_sa_id);

						// if (this.organization) {
						// 	if (ValidationTools.hasFlag(this.organization, 'restricted'))
						// 		canRegisterUserKeys = ValidationTools.checkRole(this.authUser.role, AppConstants.adminUserRole);
						// 	else
						// 		canRegisterUserKeys = ValidationTools.checkAccess(this.authUser, 'manage-basic-users-' + this.organization.otype);
						// } // if
					} // if

					this.addedByUser = null;
					if (this.activation.zcp_created_by && this.activation.zcp_created_by !== 0)
						this.addedByUser = this.usersService.getOne(this.activation.zcp_created_by);

					this.editedByUser = null;
					if (this.activation.zcp_updated_by && this.activation.zcp_updated_by !== 0)
						this.editedByUser = this.usersService.getOne(this.activation.zcp_updated_by);
				} // if

				this.keyWarnings = LicenseValidationTools.getKeyWarnings(this.activation, this.staffMode ? 'staff-enforced' : 'user', 0, 0);

				if (this.staffMode && this.keyWarnings.length === 0)
					this.nonEnforcedWarnings = LicenseValidationTools.getKeyWarnings(this.activation, 'staff-all', 0, 0);
				// this.gapWarnings = LicenseValidationTools.getKeyGapWarnings(this.activation);
				if (this.staffMode)
					this.meterWarnings = LicenseValidationTools.getMeterCommTypeWarnings(this.activation);

				this.marketplaceLogs = [];
				this.marketplaceLabel = '';
				this.marketplaceAccountIdentifier = '';
				this.marketplaceProducts = [];
				this.lastMarketplaceUsageReported = null;
				this.selectedMarketPlaceProtocolSet = null;

				if (this.staffMode && this.activation.marketplace != null && this.activation.marketplace.marketplace !== ''
					&& this.activation.marketplace.accountIdentifier && this.activation.marketplace.accountIdentifier !== ''
					&& this.activation.marketplace.productIdentifiers && this.activation.marketplace.productIdentifiers.length > 0) {

					this.marketplaceAccountIdentifier = this.activation.marketplace.accountIdentifier;
					this.marketplaceLabel = this.activation.marketplace.marketplace;
					let productSelections: any[] = [];
					const idx1 = MiscTools.findIndexGeneric(AppConstants.marketPlaceSelections, 'value', this.activation.marketplace.marketplace);
					if (idx1 !== -1) {
						this.marketplaceLabel = AppConstants.marketPlaceSelections[idx1].label + ' (' + this.activation.marketplace.marketplace + ')';
						productSelections = AppConstants.marketPlaceSelections[idx1].selections;
						this.marketPlaceAccountIdLabel = AppConstants.marketPlaceSelections[idx1].accountLabel;
					} // if

					for (const prodCode of this.activation.marketplace.productIdentifiers) {
						const idx = MiscTools.findIndexGeneric(productSelections, 'productCode', prodCode);
						if (idx === -1)
							this.marketplaceProducts.push('Unknown product code (' + prodCode + ')');
						else
							this.marketplaceProducts.push(productSelections[idx].label + '(' + prodCode + ')');
					} // for

					if (this.activation.marketplace.latestReport != null)
						this.lastMarketplaceUsageReported = new Date(this.activation.marketplace.latestReport);
					if (this.lastMarketplaceUsageReported != null && isNaN(this.lastMarketplaceUsageReported.getTime())) this.lastMarketplaceUsageReported = null;

					this.canReportToMarketplace = ValidationTools.checkAccess(this.authUser, 'report-marketplace-usage');

					let protocolSetId: number = +this.activation.marketplace.protocolSetId;
					if (isNaN(protocolSetId)) protocolSetId = 0;
					if (protocolSetId !== 0)
						this.selectedMarketPlaceProtocolSet = await this.licensingAdminService.getProtocolSet(protocolSetId);

					this.manualMarketplaceForm = new UntypedFormGroup({
						amountGB: new UntypedFormControl('', [Validators.required]),
						reportStart: new UntypedFormControl('', [Validators.required]),
						reportEnd: new UntypedFormControl('', [Validators.required])
					});
				} // if

				// if there's a tabID and the tab is visible, click it
				this.clickTab();

				if (this.staffMode) this.checkCompliance(-1, false);

				this.searchForm = new UntypedFormGroup({
					hostidFilter: new UntypedFormControl(this.hostidFilter)
				});

				// get usage data
				if (this.showMeters) {
					try {
						if (this.staffMode)
							this.monthlyMeterUsage = await this.licensingService.getActivationMonthlyUsage(this.activation.id, 'meter-data');
						else
							this.monthlyMeterUsage = await this.myKeysService.getActivationMonthlyUsage(this.userKey, 'meter-data');

						await this.workoutMeterUsage();
						this.showMeterTraffic = this.meterProductsToShow.length !== 0;
					} catch (ex: any) {
						this.uiAlertsService.addMsg('There was a problem fetching the meter data.', 'danger', '', false, 0);
						console.log(ex);
					}
				}  // if

				// if there's a tabID and the tab is visible, click it
				this.clickTab();

				if (this.protocolReady) {
					try {
						if (this.staffMode)
							this.monthlyProtocolUsage = await this.licensingService.getActivationMonthlyUsage(this.activation.id, 'protocol-data', AppConstants.groupByBillingCodeToken);
						else
							this.monthlyProtocolUsage = await this.myKeysService.getActivationMonthlyUsage(this.userKey, 'protocol-data', AppConstants.groupByBillingCodeToken);

						await this.workoutProtocolUsage();
						this.showProtocolTraffic = this.protocolProducts.length !== 0;
					} catch (ex: any) {
						this.uiAlertsService.addMsg('There was a problem fetching the protocol data.', 'danger', '', false, 0);
						console.log(ex);
					}
				} // if

				// if there's a tabID and the tab is visible, click it
				this.clickTab();

				if (this.staffMode) {
					// make up list of potential dataTypes
					// each meter type + all protocols, totals & protocol sets
					// pre-select each meter type + overall total or locked protocol sets (if any)

					let dataTypes: string[] = [];
					this.usageSummaryDataTypeSelections = [];
					for (const meterProduct of this.meterProductsToShow) {
						this.usageSummaryDataTypeSelections.push({
							value: 'meter:' + meterProduct,
							label: SharedLicenseTools.niceProtocol(meterProduct, true) + ' (M)'
						});
						dataTypes.push('meter:' + meterProduct);
					} // for
					for (const protocolProduct of this.protocolProductsToShow) {
						this.usageSummaryDataTypeSelections.push({
							value: 'protocol:' + protocolProduct,
							label: SharedLicenseTools.niceProtocol(protocolProduct, true) + ' (P)'
						});
					} // for

					for (const pSet of this.availableSets) {
						this.usageSummaryDataTypeSelections.push({
							value: 'protocol:protocolset-' + pSet.id,
							label: pSet.name + ' (PS)'
						});
					} // for

					if (localStorage.getItem('CLEAN.cp.KeyUsageSummaryReport.datatypes')) {
						try {
							const tmpDataTypes: string[] = JSON.parse(localStorage.getItem('CLEAN.cp.s'));
							if (tmpDataTypes && tmpDataTypes.length > 0) dataTypes = tmpDataTypes;
						} catch (ex: any) {
						}
					} // if

					if (dataTypes.length > 0)
						this.usageSummaryForm = new UntypedFormGroup({
							dataTypes: new UntypedFormControl(dataTypes, [Validators.required])
						});

					this.lpUser = '';
					if (this.activation.user_id && this.activation.user_id !== 0)
						this.lpUser = await this.licensingService.getClassicLicenseUser(this.activation.user_id);

					this.keySnoozes = await this.licensingService.getUserKeySnoozesForKey(this.activation.id);
					this.userSnoozes = {};
					for (const snooze of this.keySnoozes) {
						if (!this.userSnoozes[snooze.user_id]) this.userSnoozes[snooze.user_id] = [];
						this.userSnoozes[snooze.user_id].push(snooze);
					} // for

					await this.loadUserKeys();

					this.adminLogs = await this.adminLogsService.getLogs(['activation'], this.activation.id);

					this.journals = await this.adminLogsService.getJournals('activation', this.activation.id);
					if (this.journalsTable1)
						this.journalsTable1.updateContent(this.journals);

					this.marketplaceLogs = await this.marketplaceService.getMarketplaceLogs('activation', this.activation.id);

				} else {
					if (this.registeredKey) {
						this.orgUsers = await this.mySettingsService.getOrgUsers();
						this.sharedWithUserIds = await this.myKeysService.getSharedWithUsers(this.userKey);

						for (const userID of this.sharedWithUserIds) {
							const idx = MiscTools.findIndex(this.orgUsers, userID);
							if (idx !== -1) this.sharedWithUsers.push(this.orgUsers[idx]);
						}
						this.sharedWithUsers.sort((a, b) => a.name.localeCompare(b.name));
					} // if
				} // if

				this.recentUsedHosts = [];
				if (this.showMeters || this.protocolReady)
					if (this.staffMode)
						this.recentUsedHosts = await this.licensingService.getActiveHostIDs(this.activation.id);
					else
						this.recentUsedHosts = await this.myKeysService.getActiveHostIDs(this.userKey);

				// console.log('in load C pm=' + this.pageMode);

				if (this.staffMode && this.usedProtocolBillingCodes.length > 0 && !this.lotsOfBillingCodes) {
					this.loading = false; // stop loading spinner at this point and let bc summary run (slow)

					this.invalidBillingCodeEntries = await this.licensingService.getInvalidBillingCodeCache(this.activation.id, '', '');
					this.invalidBillingCodeEntries.sort((a, b) => (a.billing_code < b.billing_code) ? 1 : -1);
					this.invalidBillingCodeEntries.sort((a, b) => (a.hostid > b.hostid) ? 1 : -1);

					this.bcSummaryByHostId = await this.licensingService.getBillingCodeSummaryForKeyByHostId(this.activation.id);
				} // if

			} else if (this.pageMode === 'hostid' || this.pageMode === 'ip') {

				if (this.staffMode) {
					if (this.pageMode === 'hostid')
						this.keys = await this.licensingService.getHostActivations(this.hostid);
					else if (this.pageMode === 'ip')
						this.keys = await this.licensingService.getIPActivations(this.ip);

					this.hostKeys = {};

					if (this.pageMode === 'hostid')
						this.licenses = await this.licensingService.getHostLicenses(this.hostid);
					else if (this.pageMode === 'ip')
						this.licenses = await this.licensingService.getIPLicenses(this.ip);
				} else {
					const idx = MiscTools.findIndexGeneric(this.hostids, 'hostid', this.hostid);
					this.label = '';
					if (idx !== -1) this.label = this.hostids[idx].label;

					this.licenses = [];
					for (const license of this.activation.licenses)
						if (license.hostid === this.hostid)
							this.licenses.push(license);
				} // if

				// newest first
				this.licenses.sort((a, b) => ((new Date(a.updated_at)).getTime() < (new Date(b.updated_at)).getTime()) ? 1 : -1);

				let hasKey = false;
				let hasUsage = false;
				this.showFileFootnote = false;
				for (const item of this.licenses) {
					if (this.latestID === 0) this.latestID = item.id;

					const licenseFileInfo = SharedLicenseTools.getLicenseFileInfo(item.fulfillment);
					this.licenseMeterInitials[item.id] = licenseFileInfo.meterInitials.join(',');
					this.licenseMeterNames[item.id] = licenseFileInfo.meterNames.join(',');
					this.licenseFormats[item.id] = licenseFileInfo.format;

					this.expiryModePerActivation[item.id] = '';
					this.meterTypesPerActivation[item.id] = '';

					if (item.activation_id && item.activation_id > 0) {
						const idx = MiscTools.findIndex(this.keys, item.activation_id);
						if (idx !== -1) {
							hasKey = true;
							this.hostKeys[item.id] = this.keys[idx];
							this.meterTypesPerActivation[item.id] = this.getMeterProductNamesForActivation(this.keys[idx]);
							this.expiryModePerActivation[item.id] = SharedLicenseTools.getExpiryMode(this.keys[idx]);

							if (!this.activationIDsForObject.includes(item.activation_id)) this.activationIDsForObject.push(item.activation_id);

							if (this.doesActivationSupportUsage(this.keys[idx]) && !this.activationIDsWithUsage.includes(item.activation_id))
								this.activationIDsWithUsage.push(item.activation_id);
						} // if
					} else {
						// if the most recent license issued isn't tied to a key
						// offer option to do it...
						if (this.pageMode === 'hostid' && this.latestID === item.id && ValidationTools.checkAccess(this.authUser, 'manage-keys'))
							this.showLinkOfflineLicense = true;
					} // if

					if (this.expiryModePerActivation[item.id] === 'meter')
						this.expiryExtraActivation[item.id] = 'Licenses issued for keys with meters will regularly update their expiration date as they report meter usage.';
					else
						this.expiryExtraActivation[item.id] = '';

					if (item.fulfillment && item.fulfillment !== '')
						this.showFileFootnote = true;

					if (this.pageMode === 'hostid' && (item.first_meter_report != null || item.first_protocol_report != null))
						hasUsage = true;
				} // for

				// get labels
				if (this.staffMode && this.pageMode === 'hostid') {
					this.labels = await this.licensingService.getHostLabels(this.hostid);
					this.linkOfflineForm = new UntypedFormGroup({
						linkKey: new UntypedFormControl('', [Validators.required])
					});
				} // if

				// if there's a tabID and the tab is visible, click it
				this.clickTab();

				if (this.staffMode) {
					if (this.pageMode === 'hostid')
						await this.getHostIdPenTests();
					else if (this.pageMode === 'ip')
						await this.getIPPenTests();
				} // if

				// get usage data
				if (this.pageMode === 'hostid' && hasUsage) {
					this.loading = false;
					// if (this.activationIDsWithUsage.length > 0)
					// 	await this.filterTraffic(this.activationIDsWithUsage[0]);
					// else
					// 	await this.filterTraffic(0);

					if (this.staffMode) {
						await this.filterTraffic(this.filterID);

						this.invalidBillingCodeEntries = await this.licensingService.getInvalidBillingCodeCache(this.filterID, this.hostid, '');

					} else {
						const keyProduct = await this.licensingAdminService.getProductByName(this.activation.product);
						this.meterTypes = [];

						this.expiryExtraActivation = '';
						const expiryMode = SharedLicenseTools.getExpiryMode(this.activation);
						if (expiryMode === 'meter')
							this.expiryExtraActivation = 'Licenses issued for keys with meters will regularly update their expiration date as they report meter usage.';

						if (keyProduct && keyProduct.show_meters && keyProduct.show_meters === 2) {
							for (const meter of this.activation.meters) {
								const niceName = SharedLicenseTools.niceProtocol(meter.product, true);
								if (!this.meterTypes.includes(niceName)) this.meterTypes.push(niceName);
							}
							this.meterTypes.sort();

							this.monthlyMeterUsage = await this.myKeysService.getHostMonthlyUsage(this.userKey, this.hostid, 'meter-data');
							await this.workoutMeterUsage();

							this.showMeterTraffic = this.meterProductsToShow.length !== 0;
						}

						this.protocolReady = keyProduct.show_protocols && keyProduct.show_protocols === 2; // show to basic user
						if (this.protocolReady) {
							this.monthlyProtocolUsage = await this.myKeysService.getHostMonthlyUsage(this.userKey, this.hostid, 'protocol-data', AppConstants.groupByBillingCodeToken);
							await this.workoutProtocolUsage();
							this.showProtocolTraffic = this.protocolProducts.length !== 0;
						}
					} // if

					// console.log(this.versionHistory);
				} // if
			} else if (this.pageMode === 'aggregate') {
				this.typeOfAggregateQuery = this.typesOfQueries[0].value;
				if (localStorage.getItem(AppConstants.typeOfAggregateQueryStorageKey))
					this.typeOfAggregateQuery = localStorage.getItem(AppConstants.typeOfAggregateQueryStorageKey).trim();

				this.keysAndHostIds = '';
				if (localStorage.getItem(AppConstants.keysAndHostIdsStorageKey))
					this.keysAndHostIds = localStorage.getItem(AppConstants.keysAndHostIdsStorageKey).trim();

				const keysAndHostIds: string = localStorage.getItem(AppConstants.keysAndHostIdsStorageKey);
				if (keysAndHostIds) this.keysAndHostIds = keysAndHostIds.trim();

				// console.log('typeOfAggregateQuery = ' + this.typeOfAggregateQuery);
				// console.log('this.keysAndHostIds');
				// console.log(this.keysAndHostIds);

				this.aggregateForm = new UntypedFormGroup({
					typeOfAggregateQuery: new UntypedFormControl(this.typeOfAggregateQuery),
					keysAndHostIds: new UntypedFormControl(this.keysAndHostIds)
				});

				this.aggregateSelectionForm = new UntypedFormGroup({});

				// if (this.keysAndHostIds && this.keysAndHostIds !== '') {
				// 	await this.processAggregateItems();
				// } //if
			} // if

			this.actionsReady = true;

			// if there's a tabID and the tab is visible, click it
			this.clickTab();

			this.workoutUsageBuffers();
		} catch (exc) {
			console.log(exc);
		}

		this.firstLoad = false;
		this.loading = false;

		await MiscTools.delay(100);
		if (this.logsTable1)
			this.logsTable1.updateContent(this.adminLogs, 'WIPE-FILTER.' + this.storageKeyPrefix + 'admin-logs', { showUserInfo: true, showObjectInfo: false, linkObject: false, linkUser: true });

		if (this.marketPlaceLogsTable)
			this.marketPlaceLogsTable.updateContent(this.marketplaceLogs, 'WIPE-FILTER.' + this.storageKeyPrefix + 'mktplace-logs', { showObject: false });

		if (this.staffMode && this.pageMode === 'hostid' && this.versionHistory.length === 0) {
			this.historyLoading = true;
			this.versionHistory = await this.licensingService.getVersionHistory(this.hostid);
			this.historyLoading = false;
		} // if
	}

	// *********************************************************
	// if there's a tabID to click on and the tab is exposed, click it
	clickTab() {
		if (this.tabID && this.tabID !== '' && document.getElementById(this.tabID)) {
			document.getElementById(this.tabID).click();
			this.tabID = '';
		} // if
	} // 

	// *********************************************************
	saveSettings() {
		let settings: any = {
			rotateAxis: this.rotateAxis,
			showRawData: this.showRawData,
			protocolSetsPosition: this.protocolSetsPosition,
			billingCodeDisplay: this.billingCodeDisplay,
			onlyShowTotals: this.onlyShowTotals,
			showNoUseDays: this.showNoUseDays,
			showMeterProtocolEquiv: this.showMeterProtocolEquiv,
			newToOld: this.newToOld,
			includeCurrentMonth: this.includeCurrentMonth
		};
		localStorage.setItem(this.storageKeyPrefix + '.settings', JSON.stringify(settings));
	}

	// *********************************************************
	// for activation page mode
	// *********************************************************

	// *********************************************************
	async loadUserKeys() {
		this.userKeys = await this.licensingService.getKeyUsers(this.activation.id);

		let canRegisterUserKeys = ValidationTools.checkAccess(this.authUser, 'manage-keys');;
		this.otherOrgUsers = [];
		if (canRegisterUserKeys && this.organization) {
			this.organizationForUsers = this.organization;
			const allOrgUsers = this.usersService.getUsersForOrganization(this.activation.zcp_org_id);
			for (const user of allOrgUsers) {
				if (user.is_enabled === 1) {
					const idx = MiscTools.findIndexGeneric(this.userKeys, 'user_id', user.id);
					if (idx === -1)
						this.otherOrgUsers.push(user);
				} // if
			} // for

			if (this.otherOrgUsers.length === 0 && this.organization && this.organization.parent_org_id && this.organization.parent_org_id !== 0) {
				this.organizationForUsers = this.organizationsService.getOne(this.organization.parent_org_id);
				const parentOrgUsers = this.usersService.getUsersForOrganization(this.organization.parent_org_id);
				for (const user of parentOrgUsers) {
					if (user.is_enabled === 1) {
						const idx = MiscTools.findIndexGeneric(this.userKeys, 'user_id', user.id);
						if (idx === -1)
							this.otherOrgUsers.push(user);
					} // if
				} // for
			} // if
		} // if
	}

	// *********************************************************
	getOverallPenTestCode(penTest: Models.BroadcasterPenTest[]) {
		let anyOpenToInternet: boolean = false;
		let anyNotOpenToInternet: boolean = false;

		for (const p of penTest) {
			if (p.test_code === 0) {
				return 0;
			} else if (p.test_code === 1) {
				anyOpenToInternet = true;
			} else if (p.test_code === 2) {
				anyNotOpenToInternet = true;
			} // if
		} // for

		if (anyOpenToInternet)
			return 1;
		else if (anyNotOpenToInternet)
			return 2;

		return -99;
	}

	// *********************************************************
	async getActivationPenTests() {
		this.allPenTests = await this.licensingService.getActivationPenTests(this.activation.id);
		this.failedPenTests = [];
		this.failedPenTestIPs = [];

		for (const item of this.activation.licenses) {
			// see if this hostid + ip has a failed pen test...
			const idx = MiscTools.findIndexGenericDouble(this.allPenTests, 'hostid', item.hostid, 'ip', item.ip);
			if (idx !== -1) { // has at least on failed pen test
				const testsForThisHostIP: Models.BroadcasterPenTest[] = [];
				for (const p of this.allPenTests)
					if (p.activation_id === item.activation_id && item.hostid === p.hostid && item.ip === p.ip)
						testsForThisHostIP.push(p);

				for (const p of testsForThisHostIP) {
					if (p.test_code === 0) {
						if (!this.failedPenTestIPs.includes(p.ip)) this.failedPenTestIPs.push(p.ip);
						const idx2 = MiscTools.findIndexGenericTriple(this.failedPenTests, 'hostid', p.hostid, 'ip', p.ip, 'username', p.username);
						if (idx2 == -1) this.failedPenTests.push(p);
					} // if
				} // for

				if (this.hostIdCounts[item.hostid] === 1 || this.licenseCounters[item.id] === this.hostIdCounts[item.hostid]) {
					item['__penTest'] = testsForThisHostIP;
					item['__penTestCode'] = this.getOverallPenTestCode(testsForThisHostIP);
					item['__penTestLabel'] = AppConstants.penTestCodes[item['__penTestCode']].label;
				} // if
			} // if
		} // for
	} //

	// *********************************************************
	async getHostIdPenTests() {
		this.allPenTests = await this.licensingService.getHostIdPenTests(this.hostid);
		this.failedPenTests = [];
		this.failedPenTestIPs = [];
		for (const item of this.licenses) {
			const idx = MiscTools.findIndexGenericTriple(this.allPenTests, 'activation_id', item.activation_id, 'hostid', item.hostid, 'ip', item.ip);
			if (idx !== -1 && item.product.startsWith('broadcaster')) { // has at least on failed pen test
				const testsForThisHostIP: Models.BroadcasterPenTest[] = [];
				for (const p of this.allPenTests)
					if (p.activation_id === item.activation_id && item.hostid === p.hostid && item.ip === p.ip)
						testsForThisHostIP.push(p);

				for (const p of testsForThisHostIP) {
					if (p.test_code === 0) {
						if (!this.failedPenTestIPs.includes(p.ip)) this.failedPenTestIPs.push(p.ip);
						const idx2 = MiscTools.findIndexGenericTriple(this.failedPenTests, 'hostid', p.hostid, 'ip', p.ip, 'username', p.username);
						if (idx2 == -1) this.failedPenTests.push(p);
					} // if
				} // for

				item['__penTest'] = testsForThisHostIP;
				item['__penTestCode'] = this.getOverallPenTestCode(testsForThisHostIP);
				item['__penTestLabel'] = AppConstants.penTestCodes[item['__penTestCode']].label;
			} // if
		} // for
	} //

	// *********************************************************
	async getIPPenTests() {
		this.allPenTests = await this.licensingService.getIPPenTests(this.ip);
		this.failedPenTests = [];
		this.failedPenTestIPs = [];
		for (const item of this.licenses) {
			const idx = MiscTools.findIndexGenericTriple(this.allPenTests, 'activation_id', item.activation_id, 'hostid', item.hostid, 'ip', item.ip);
			if (idx !== -1 && item.product.startsWith('broadcaster')) { // has at least on failed pen test
				const testsForThisHostIP: Models.BroadcasterPenTest[] = [];
				for (const p of this.allPenTests)
					if (p.activation_id === item.activation_id && item.hostid === p.hostid && item.ip === p.ip)
						testsForThisHostIP.push(p);

				for (const p of testsForThisHostIP) {
					if (p.test_code === 0) {
						if (!this.failedPenTestIPs.includes(p.ip)) this.failedPenTestIPs.push(p.ip);
						const idx2 = MiscTools.findIndexGenericTriple(this.failedPenTests, 'hostid', p.hostid, 'ip', p.ip, 'username', p.username);
						if (idx2 == -1) this.failedPenTests.push(p);
					} // if
				} // for

				item['__penTest'] = testsForThisHostIP;
				item['__penTestCode'] = this.getOverallPenTestCode(testsForThisHostIP);
				item['__penTestLabel'] = AppConstants.penTestCodes[item['__penTestCode']].label;
			} // if
		} // for
	} //

	// *********************************************************
	async runPenTestOnIPs(ips: string[], username: string) {
		this.penTestRunning = true;

		try {
			await this.licensingService.runPenTestOnIPs(ips, username);

			if (this.pageMode === 'activation') {
				await this.getActivationPenTests();
			} else if (this.pageMode === 'hostid') {
				await this.getHostIdPenTests();
			} else if (this.pageMode === 'ip') {
				await this.getIPPenTests();
			} // if

			this.uiAlertsService.addMsg('Testing finished.', 'info', '', false, AppConstants.standardMessageAutoCloseTimeSecs);
		} catch (e: any) {
			if (e.message)
				this.uiAlertsService.addMsg(e.message, 'danger', '', false, AppConstants.standardMessageAutoCloseTimeSecs);
			else
				this.uiAlertsService.addMsg('There was a problem', 'danger', '', false, AppConstants.standardMessageAutoCloseTimeSecs);
		}

		this.penTestRunning = false;
	} //

	// *********************************************************
	async shareKeyWithUser(userToShareWith: Models.User) {
		if (confirm('Are you sure you want to share this key with this user?')) {
			try {
				this.loading = true;
				await this.usersService.registerKeys(userToShareWith.id, [{ key: this.activation.key, label: '' }], false, 'zcp-staff-from-key-view');
				await this.loadUserKeys();
				this.uiAlertsService.addMsg('This key has been shared with ' + userToShareWith.name + '.', 'info', '', false, AppConstants.standardMessageAutoCloseTimeSecs);
			} catch (e: any) {
				this.uiAlertsService.addMsg(e.message, 'danger', '', false, AppConstants.standardMessageAutoCloseTimeSecs);
			}
			this.loading = false;
		} // if
	} //

	// *********************************************************
	async doSearch() {
		this.hostidFilter = this.searchForm.value.hostidFilter;

		if (!this.hostidFilter || this.hostidFilter === '')
			return;

		this.searching = true;
		this.showSearchResults = false;

		let tmpSearchResults: Models.LPLicense[] = [];

		if (this.staffMode)
			tmpSearchResults = await this.licensingService.searchHosts(this.activation.id, this.hostidFilter);
		else
			tmpSearchResults = await this.myKeysService.searchHosts(this.userKey, this.hostidFilter);

		if (tmpSearchResults && tmpSearchResults.length > AppConstants.maxLicenseRowsToFetch) {
			tmpSearchResults.sort((a, b) => ((new Date(a.created_at)).getTime() < (new Date(b.created_at)).getTime()) ? 1 : -1);
			tmpSearchResults.pop(); // remove the last one..
			// this.maxMessage = 'Maximum keys (' + AppConstants.maxLicenseRowsToFetch + ') retrieved.';
		} else {
			// this.maxMessage = '';
		}

		if (this.staffMode) {
			for (const item of tmpSearchResults) {
				const idx = MiscTools.findIndexGeneric(this.allPenTests, 'ip', item.ip);
				if (idx !== -1) {
					item['__penTest'] = this.allPenTests[idx];
					if (this.allPenTests[idx].test_code === 0) {
						const idx2 = MiscTools.findIndexGenericDouble(this.failedPenTests, 'hostid', item.hostid, 'ip', item.ip);
						if (idx2 == -1) this.failedPenTests.push(this.allPenTests[idx]);
						if (!this.failedPenTestIPs.includes(item.ip)) this.failedPenTestIPs.push(item.ip);
					} // if
				} // if
			} // for
		} // if

		this.searchResults = tmpSearchResults;

		for (const item of this.searchResults) {
			if (this.hostidLabels[item.hostid])
				item['__label'] = this.hostidLabels[item.hostid]
		} // for

		this.searching = false;
		this.showSearchResults = true;

		await MiscTools.delay(100);
		this.licenseTableOptions = { staffMode: this.staffMode, showMeters: this.showMeters, protocolReady: this.protocolReady, key: this.activation ? this.activation.key : '' };
		if (this.searchLicensesTable)
			this.searchLicensesTable.updateContent(this.searchResults, 'WIPE-FILTER.' + this.storageKeyPrefix + '-search-licenses', this.licenseTableOptions);
	}

	// *********************************************************
	getNewLicenses() {
		const url = AppConstants.apiUrl + AppConstants.apiUrls.licensing + '/activation/' + this.activation.id + '/zip-new-licenses';
		window.open(url, '_blank');
	}

	// *********************************************************
	clone() {
		if (confirm('Are you sure you want to clone/copy this key?'))
			this.router.navigate(['/' + AppConstants.urls.licensing, this.activation.id, 'clone']);
	}

	// *********************************************************
	async getSalesforceInfoBlocks() {
		this.sfFetching = true;
		if (this.organization && this.organization.salesforce_account_id && this.organization.salesforce_account_id !== '')
			this.salesforceAccountInfoBlock = await this.organizationsService.makeSalesForceObjectBlock('accounts', this.organization.salesforce_account_id);

		if (this.activation.opportunity_id && this.activation.opportunity_id !== '')
			this.salesforceOpportunityInfoBlock = await this.organizationsService.makeSalesForceObjectBlock('opportunities', this.activation.opportunity_id);

		this.sfFetching = false;
	}

	// *********************************************************
	async workoutHostIDProperties() {
		this.hostIdCounts = {};
		this.licenseCounters = {};
		this.licenseMeterNames = {};
		this.licenseMeterInitials = {};

		if (!this.activation || !this.activation.licenses || this.activation.licenses.length === 0)
			return;

		// sort licenses by created date (newest to oldest)
		this.activation.licenses.sort((a, b) => ((new Date(a.created_at)).getTime() < (new Date(b.created_at)).getTime()) ? 1 : -1);

		this.showMaxHostsExtra = false;
		if (this.activation.licenses.length > AppConstants.maxLicenseRowsToFetch) {
			this.showMaxHostsExtra = true;
			while (this.activation.licenses.length > AppConstants.maxLicenseRowsToFetch) {
				this.activation.licenses.pop();
			} // while
			if (this.staffMode)
				this.fullLicenseCount = await this.licensingService.getNumLicenses(this.activation.id);
			else
				this.fullLicenseCount = await this.myKeysService.getNumLicenses(this.activation.key);
		} // if

		let minRecentLicenseDays = 99;
		let recentCount = 0;

		this.activeLicenses = [];
		let tmpActive: Models.LPLicense[] = [];
		// go through all of them and count each hostid
		for (const item of this.activation.licenses) {
			const days = MiscTools.daysSince(item.created_at);
			if (days < AppConstants.maxDaysForRecentLicenses) recentCount++;
			if (days < minRecentLicenseDays) minRecentLicenseDays = days;

			if (!this.showMaxHostsExtra && SharedLicenseTools.licenseRecentlyReported(item, AppConstants.maxActiveLicenseDays))
				tmpActive.push(item);
		} // for

		if (this.showMaxHostsExtra && this.activation.discard_licenses === 0) {
			// call back end to get active licenses
			if (this.staffMode)
				this.activeLicenses = await this.licensingService.getKeysActiveLicenses(this.activation.id, AppConstants.maxActiveLicenseDays);
			else
				this.activeLicenses = await this.myKeysService.getKeysActiveLicenses(this.userKey, AppConstants.maxActiveLicenseDays);
		} else {
			this.activeLicenses = tmpActive;
		} // if

		// go through them again and assign count for ones where count > 1

		// sort oldest to newest
		this.activation.licenses.sort((a, b) => ((new Date(a.created_at)).getTime() > (new Date(b.created_at)).getTime()) ? 1 : -1);

		const hostCounters: any = {};
		for (const item of this.activation.licenses) {
			if (this.hostIdCounts[item.hostid] > 1) {
				if (!hostCounters[item.hostid]) hostCounters[item.hostid] = 0;
				hostCounters[item.hostid]++;
				this.licenseCounters[item.id] = hostCounters[item.hostid];
			}  // if

			if (this.hostidLabels[item.hostid])
				item['__label'] = this.hostidLabels[item.hostid]
		} // for

		for (const item of this.activeLicenses) {
			if (this.hostidLabels[item.hostid])
				item['__label'] = this.hostidLabels[item.hostid]
		} // for

		this.showDownloadRecentButton = this.staffMode && (recentCount < 50 && minRecentLicenseDays < AppConstants.maxDaysForRecentLicenses);

		if (this.staffMode) {
			for (const item of this.invalidBillingCodeEntries)
				if (MiscTools.daysSince(item.max_day, false) < 2)
					item['_active'] = true;
			await this.getActivationPenTests();
		} // if
	}

	// *********************************************************
	async runLicenseReport() {
		if (this.staffMode)
			await this.reportsService.runReport('KeyLicensesReport', 'id=' + this.activation.id);
		else
			await this.reportsService.runReport('KeyLicensesReport', 'key=' + encodeURIComponent(this.userKey.activation_key));
	}

	// *********************************************************
	async runBillingCodeSummaryReport() {
		if (this.staffMode)
			await this.reportsService.runReport('BillingCodesForKeyReport', 'id=' + this.activation.id);
	}

	// *********************************************************
	async runKeyUsageSummaryReport() {
		if (this.staffMode) {
			const dataTypes: string[] = this.usageSummaryForm.value.dataTypes;
			if (dataTypes && dataTypes.length > 0) {
				localStorage.setItem('CLEAN.cp.KeyUsageSummaryReport.datatypes', JSON.stringify(dataTypes))
				await this.reportsService.runReport('KeyUsageSummaryReport', 'id=' + this.activation.id
					+ '&dataTypes=' + encodeURIComponent(dataTypes.join(',')));

				UITools.clickButton('close-usage-summary-modal');
			} // if
		} // if
	}

	// *********************************************************
	async enableKey() {
		if (confirm('Are you sure you want to enable this key?')) {
			try {
				await this.licensingService.enableActivation(this.activation);
				this.activation.enabled = 1;
				this.uiAlertsService.addMsg('The key has been enabled.',
					'info', '', false, AppConstants.standardMessageAutoCloseTimeSecs);
			} catch (e: any) {
				this.uiAlertsService.addMsg(e.message, 'danger', '', false, AppConstants.standardMessageAutoCloseTimeSecs);
			}
		} // if

	}
	// *********************************************************
	prepDisable() {
		this.popupBox.openPopup('confirm-text', 'disableKey', [], 'Disable Key',
			'If you disable this Key, new activations will not be possible and systems already activated and running may be affected.',
			'ban',
			{ confirmButtonText: 'Disable Key', rejectButtonText: 'Cancel', confirmText: 'disable' });
	} //

	// *********************************************************
	async disableKey() {
		try {
			await this.licensingService.disableActivation(this.activation);
			this.activation.enabled = 0;

			this.uiAlertsService.addMsg('The key has been disabled.',
				'info', '', false, AppConstants.standardMessageAutoCloseTimeSecs);
		} catch (e: any) {
			this.uiAlertsService.addMsg(e.message, 'danger', '', false, AppConstants.standardMessageAutoCloseTimeSecs);
		}
	}

	// *********************************************************
	async checkCompliance(templateId: number, doAlert: boolean = true) {
		this.checkingCompliance = true;

		if (templateId === -1 && this.template) templateId = this.template.id;
		if (this.template && templateId === this.template.id) this.isCompliant = false;

		const idx = MiscTools.findIndex(this.allTemplates, templateId);
		if (idx !== -1) {
			const templateComplianceIssues: string[] = LicenseValidationTools.checkActivation(this.activation, this.allTemplates[idx], this.keyProduct, this.keyProductProperties, this.organization, false, true);

			const idx2 = templateComplianceIssues.indexOf('The key\'s template ID does not match the template\'s ID.');
			if (idx2 !== -1) templateComplianceIssues.splice(idx2, 1);

			if (templateComplianceIssues.length === 0 && templateId === this.template.id)
				this.isCompliant = true;

			if (doAlert) {
				if (templateComplianceIssues.length > 0)
					this.uiAlertsService.addMsg('This key is NOT compliant with \'' + this.allTemplates[idx].name + '\'.\n\u2022 ' + templateComplianceIssues.join('\n\u2022 '), 'warning', 'compliance', true, AppConstants.standardMessageAutoCloseTimeSecs * 4);
				else
					this.uiAlertsService.addMsg('This key is compliant with \'' + this.allTemplates[idx].name + '\'.', 'success', 'compliance', true, AppConstants.standardMessageAutoCloseTimeSecs);

				await MiscTools.delay(1000);
			} // if
		}
		this.checkingCompliance = false;
	}

	// *********************************************************
	makeSnoozeTooltip(userId: number) {
		let tip = '';
		if (this.userSnoozes[userId].length > 0) {
			let snoozeTypes: string[] = [];
			for (const snooze of this.userSnoozes[userId]) {
				const exp = new Date(snooze.expires_at);
				let snoozeText = MiscTools.fetchLabel(AppConstants.keySnoozeTypes, snooze.snooze_type);
				if (snooze.expires_at != null && !isNaN(exp.getTime()))
					snoozeText += ' (expires ' + TextTools.formatDateNiceUTC(exp) + ')';
				snoozeTypes.push(snoozeText);
			} // for

			snoozeTypes = MiscTools.removeDuplicates(snoozeTypes)
			snoozeTypes.sort();
			tip = 'This user has snoozes/pauses of the following type(s) for this key: ' + snoozeTypes.join(', ');
		} // if
		return tip;
	}

	// *********************************************************
	getMarketPlaceProductName(log: Models.MarketplaceUsageReport) {
		const key = log.marketplace + ':' + log.product_id;
		if (!this.productCache[key]) {
			const idx1 = MiscTools.findIndexGeneric(AppConstants.marketPlaceSelections, 'value', log.marketplace);
			if (idx1 !== -1) {
				const idx2 = MiscTools.findIndexGeneric(AppConstants.marketPlaceSelections[idx1].selections, 'productCode', log.product_id);
				if (idx2 !== -1)
					this.productCache[key] = AppConstants.marketPlaceSelections[idx1].selections[idx2].label;
			} // if
		} // if

		if (this.productCache[key])
			return this.productCache[key]

		return log.product_id;
	}

	// *********************************************************
	async manualMarketplaceKeyReport() {

		const errors: string[] = [];
		// check form elements to get args

		let amountGB: number = +this.manualMarketplaceForm.value.amountGB;
		let reportStart: Date = new Date(this.manualMarketplaceForm.value.reportStart + ' UTC');
		let reportEnd: Date = new Date(this.manualMarketplaceForm.value.reportEnd + ' UTC');

		if (isNaN(reportStart.getTime())) reportStart = null;
		if (isNaN(reportEnd.getTime())) reportEnd = null;

		if (isNaN(amountGB) || amountGB <= 0)
			errors.push('Amount (GB) must be a postive number.');

		if (reportStart == null)
			errors.push('Start of Report is not a valid date/time.');

		if (reportEnd == null)
			errors.push('End of Report is not a valid date/time.');

		if (errors.length > 0) {
			this.uiAlertsService.addMsgs(errors, 'warning', '', false, AppConstants.standardMessageAutoCloseTimeSecs);
			return;
		} // if
		UITools.clickButton('close-manual-marketplace-report-modal');

		try {
			const ret = await this.marketplaceService.manualKeyReport(this.activation.id, reportStart, reportEnd, amountGB);
			this.uiAlertsService.addMsg('The usage has been reported.', 'info', '', false, AppConstants.standardMessageAutoCloseTimeSecs);
		} catch (e: any) {
			this.uiAlertsService.addMsg(e.message, 'danger', '', false, AppConstants.standardMessageAutoCloseTimeSecs);
		}
	}

	// --------------------------------------------------------------------
	getCommercialTypeLabel(commercialType: string = '') {
		if (commercialType && commercialType !== '') {
			const cIdx = MiscTools.findIndexGeneric(AppConstants.keyCommercialTypes, 'value', commercialType);
			if (cIdx !== -1)
				return AppConstants.keyCommercialTypes[cIdx].label;
		} // if
		return commercialType;
	}

	// --------------------------------------------------------------------
	getCommercialTypeInfo(commercialType: string = '') {
		if (commercialType && commercialType !== '') {
			const cIdx = MiscTools.findIndexGeneric(AppConstants.keyCommercialTypes, 'value', commercialType);
			if (cIdx !== -1)
				return AppConstants.keyCommercialTypes[cIdx].info;
		} // if
		return commercialType;
	}

	// *********************************************************
	// for hostid page mode
	// *********************************************************

	// *********************************************************
	async filterTraffic(activationID: number) {
		if (this.loading) return;
		// if (activationID === this.filterID) activationID = 0; // toggle off

		this.loading = true;
		this.filterID = 0;
		this.filterKey = '';

		this.resetMeterUsage();
		this.resetProtocolUsage();

		if (activationID > 0) {
			// find the key and  			
			const idx = MiscTools.findIndex(this.keys, activationID);
			if (idx !== -1) {
				this.filterID = activationID;
				this.filterKey = this.keys[idx].key;
			}
		}

		this.monthlyMeterUsage = await this.licensingService.getHostMonthlyUsage(this.hostid, 'meter-data', '', this.filterID);
		await this.workoutMeterUsage();

		this.monthlyProtocolUsage = await this.licensingService.getHostMonthlyUsage(this.hostid, 'protocol-data', AppConstants.groupByBillingCodeToken, this.filterID);
		await this.workoutProtocolUsage();

		if (!this.showMeterTraffic) this.showMeterTraffic = this.meterProductsToShow.length !== 0;
		if (!this.showProtocolTraffic) this.showProtocolTraffic = this.protocolProducts.length !== 0;

		this.loading = false;
	}

	// *********************************************************
	niceKeyProduct(product: string) {
		const idx = MiscTools.findIndexGeneric(this.keyProducts, 'name', product);
		if (idx === -1)
			return product;
		else
			return this.keyProducts[idx].label;
	}

	// *********************************************************
	downloadLicenseFiles(id: number, addHostID = false) {
		let url = AppConstants.apiUrl + AppConstants.apiUrls.licensing + '/license/' + id;
		if (addHostID) url += '?addHostID=yes';
		window.open(url, '_blank');
	}

	// *********************************************************
	getMeterProductNamesForActivation(key: Models.LPActivation) {
		const names = [];
		for (const meter of key.meters) {
			const niceName = SharedLicenseTools.niceProtocol(meter.product, true);
			if (!names.includes(niceName)) names.push(niceName);
		}
		names.sort();
		return names.join(', ');
	}

	// *********************************************************
	doesActivationSupportUsage(key: Models.LPActivation) {
		const idx2 = MiscTools.findIndexGeneric(this.keyProducts, 'name', key.product);
		return (idx2 !== -1 && (this.keyProducts[idx2].show_meters > 0 || this.keyProducts[idx2].show_protocols > 0));
	}

	// *********************************************************
	async showLicenseFeatures(id: number) {
		this.showSelectedKeyLicFeatures = false;

		// get this license...
		const licIdx = MiscTools.findIndex(this.licenses, id);
		if (licIdx !== -1) {
			const selectedKeyLicParsedParameters: any = SharedLicenseTools.parseRubyHash(this.licenses[licIdx].parameters);
			this.showSelectedKeyLicFeatures = true;
			await MiscTools.delay(200);
			if (this.hostFeatures) {
				await this.hostFeatures.updateContent(selectedKeyLicParsedParameters, this.licenses[licIdx].product, { staffMode: this.staffMode, hostMode: true });
				UITools.clickButton('open-license-features-panel');
			} // if
		} // if
	}

	// *********************************************************
	// for aggregate page mode
	// *********************************************************
	async updateKeysAndHostIds() {

		// get check items...

		let newKeysAndHostIDs = '';

		let counter = 0;
		for (const activationIdAndHostIdsCombo of this.activationIdAndHostIdsCombos) {
			const currCheckValue = this.aggregateSelectionForm.value['select_' + counter];
			if (currCheckValue) {
				if (activationIdAndHostIdsCombo.hostid && activationIdAndHostIdsCombo.hostid !== '') {
					newKeysAndHostIDs += this.getFieldFromResolveData(activationIdAndHostIdsCombo['activation_id'], 'key')
						+ ':' + activationIdAndHostIdsCombo.hostid
						+ '\n';
				} else {
					newKeysAndHostIDs += this.getFieldFromResolveData(activationIdAndHostIdsCombo['activation_id'], 'key') + '\n';
				}
			}
			counter++;
		} // for

		this.aggregateForm.controls.keysAndHostIds.setValue(newKeysAndHostIDs);
		await this.processAggregateItems();
	}

	// *********************************************************
	async processAggregateItems() {
		this.resolving = true;

		this.uiAlertsService.addMsg('Getting usage for items.', 'info', 'aggregate_resolve', false, AppConstants.standardMessageAutoCloseTimeSecs);

		this.aggregateSelectionForm = null;
		this.protocolBillingCodeToSearch = '';
		this.resolveProblems = [];
		this.keysAndHostIdsCombos = [];
		this.activationIdAndHostIdsCombos = [];
		this.resetMeterUsage();
		this.resetProtocolUsage();

		this.typeOfAggregateQuery = this.aggregateForm.value.typeOfAggregateQuery;
		this.keysAndHostIds = this.aggregateForm.value.keysAndHostIds;

		localStorage.setItem(AppConstants.keysAndHostIdsStorageKey, this.keysAndHostIds);
		localStorage.setItem(AppConstants.typeOfAggregateQueryStorageKey, this.typeOfAggregateQuery);

		let hasHosts: boolean = false;
		let splitList: string[] = this.keysAndHostIds.split('\n');
		splitList = MiscTools.removeDuplicates(splitList);

		if (this.typeOfAggregateQuery === 'keys') {
			for (const splitItem of splitList) {
				if (splitItem.includes(':'))
					this.resolveProblems.push('A line has a colon in it and should not ("' + splitItem + '").');
				else if (splitItem.trim() !== '')
					this.keysAndHostIdsCombos.push({ key: splitItem.trim(), hostid: '' });
			} // for
			hasHosts = false;

		} else if (this.typeOfAggregateQuery === 'hostids') {
			for (const splitItem of splitList) {
				if (splitItem.includes(':'))
					this.resolveProblems.push('A line has a colon in it and should not ("' + splitItem + '").');
				else if (splitItem.trim() !== '')
					this.keysAndHostIdsCombos.push({ key: '', hostid: splitItem.trim() });
			} // for
			hasHosts = true;

		} else if (this.typeOfAggregateQuery === 'mix') {
			for (const splitItem of splitList) {
				if (splitItem.trim() !== '') {
					if (splitItem.includes(':')) {
						this.keysAndHostIdsCombos.push({
							key: TextTools.getLabel(splitItem).trim(),
							hostid: TextTools.getValue(splitItem).trim()
						});
						hasHosts = true;
					} else {
						this.resolveProblems.push('A line does not have a colon in it and it should ("' + splitItem + '").');
					} // if
				} // if
			} // for
		} // if

		// console.log('this.resolveProblems');
		// console.log(this.resolveProblems);
		// console.log('this.keysAndHostIdsCombos');
		// console.log(this.keysAndHostIdsCombos);

		// call backend to resolve keys and hostIDs to get activation objects and licenses
		// build array of { "activation_id": #### } or { "activation_id": ####, "hostid": "aaabbb111" }
		// not any that can't be resolved...

		if (this.keysAndHostIdsCombos.length > 0) {
			try {
				this.resolveData = await this.licensingService.gatherKeyAndHostInfo(this.keysAndHostIdsCombos);

				if (!hasHosts && (!this.resolveData.activations || this.resolveData.activations.length === 0)) {
					this.resolveProblems.push('No resolved keys returned.');
				} else if (hasHosts && (!this.resolveData.licenses || this.resolveData.licenses.length === 0)) {
					this.resolveProblems.push('No resolved licenses returned.');
				} else {
					for (const keysAndHostIdsCombo of this.keysAndHostIdsCombos) {
						const idx1 = MiscTools.findIndexGeneric(this.resolveData.activations, 'key', keysAndHostIdsCombo.key);
						if (idx1 === -1) {
							this.resolveProblems.push('No resolved object for key (' + keysAndHostIdsCombo.key + ').');
						} else {
							const theKey: Models.LPActivation = this.resolveData.activations[idx1];

							if (theKey.protocolSets) {
								// console.log(theKey.key + ' has protocol sets');
								for (const set of theKey.protocolSets) {
									const theSet: Models.ProtocolSet = MiscTools.pickItem(this.availableSets, 'id', set.set_id);
									const idx: number = MiscTools.findIndex(this.selectedProtocolSets, set.set_id);
									if (theSet && idx === -1)
										this.selectedProtocolSets.push(theSet);
								} // for
							} // if

							const actID: number = theKey.id;
							let sortKey: string = theKey.product
								+ ':' + theKey.type
								+ ':' + theKey.info
								+ ':' + theKey.id;

							if (keysAndHostIdsCombo.hostid && keysAndHostIdsCombo.hostid !== '') {
								sortKey += ':' + keysAndHostIdsCombo.hostid;
								const idx2 = MiscTools.findIndexGenericDouble(this.resolveData.licenses, 'hostid', keysAndHostIdsCombo.hostid, 'activation_id', actID);
								if (idx2 === -1) {
									this.resolveProblems.push('No resolved object for key (' + keysAndHostIdsCombo.key + ') and hostid (' + keysAndHostIdsCombo.hostid + ').');
								} else {
									this.activationIdAndHostIdsCombos.push({ activation_id: actID, hostid: keysAndHostIdsCombo.hostid, sortKey });
								} // if
							} else {
								this.activationIdAndHostIdsCombos.push({ activation_id: actID, sortKey });
							} // if
						} // if
					} // for
				} // if

				this.activationIdAndHostIdsCombos.sort((a, b) => (a.sortKey > b.sortKey) ? 1 : -1);
			} catch (error: any) {
				this.resolveProblems.push(error.message);
			} // try

			if (this.resolveProblems.length > 0) {
				this.resolving = false;
				this.loading = false;
				return;
			} // if

			// put in check box (checked) for each activationIdAndHostIdsCombos
			const formElements: any = { select_all: new UntypedFormControl(true) };
			let counter = 0;
			for (const activationIdAndHostIdsCombo of this.activationIdAndHostIdsCombos) {
				formElements['select_' + counter] = new UntypedFormControl(true)
				counter++;
			} // for
			this.aggregateSelectionForm = new UntypedFormGroup(formElements);

			UITools.clickButton('nav-item-details-tab');

			this.loading = true;

			this.monthlyMeterUsage = await this.licensingService.getAggregateMonthlyUsage(this.activationIdAndHostIdsCombos, 'meter-data', '');
			await this.workoutMeterUsage();

			this.monthlyProtocolUsage = await this.licensingService.getAggregateMonthlyUsage(this.activationIdAndHostIdsCombos, 'protocol-data', AppConstants.groupByBillingCodeToken);
			await this.workoutProtocolUsage();

			if (!this.showMeterTraffic) this.showMeterTraffic = this.meterProductsToShow.length !== 0;
			if (!this.showProtocolTraffic) this.showProtocolTraffic = this.protocolProducts.length !== 0;
		} // if

		this.uiAlertsService.clearMsgByCode('aggregate_resolve');
		this.loading = false;
		this.resolving = false;
	} // processAggregateItems

	// *********************************************************
	getFieldFromResolveData(activationID: number, field: string) {
		if (this.resolveData && this.resolveData.activations) {
			const idx = MiscTools.findIndexGeneric(this.resolveData.activations, 'id', activationID);
			if (idx !== -1) {
				if (field === 'key') {
					return this.resolveData.activations[idx].key;

				} else if (field === 'organization') {
					if (this.resolveData.activations[idx].zcp_org_id && +this.resolveData.activations[idx].zcp_org_id !== 0) {
						const org = this.organizationsService.getOne(+this.resolveData.activations[idx].zcp_org_id);
						if (org) return org.name;
					} // if
				} else if (field === 'info') {
					if (this.resolveData.activations[idx].info && this.resolveData.activations[idx].info !== '')
						return this.resolveData.activations[idx].info;
					else
						return this.resolveData.activations[idx].customer;

				} else if (field === 'product') {
					return this.niceKeyProduct(this.resolveData.activations[idx].product);
				} else if (field === 'type') {
					return TextTools.capitalizeFirstLetter(this.resolveData.activations[idx].type);
				} else if (field === 'commercial_type') {
					return SharedLicenseTools.getCommercialTypeAcronym(this.resolveData.activations[idx]);
				} // if
			} // if
		} // if
		return '';
	}

	// *********************************************************
	getActivationFromResolveData(activationID: number) {
		if (this.resolveData && this.resolveData.activations) {
			const idx = MiscTools.findIndexGeneric(this.resolveData.activations, 'id', activationID);
			if (idx !== -1)
				return this.resolveData.activations[idx];
		} // if
		return null;
	}

	// ***************************************************************
	checkAllToggle() {
		const currCheckAllValue = this.aggregateSelectionForm.value['select_all'];
		// uncheck everything

		let counter = 0;
		for (const activationIdAndHostIdsCombo of this.activationIdAndHostIdsCombos) {
			this.aggregateSelectionForm.controls['select_' + counter].setValue(false);
			counter++;
		} // for

		if (currCheckAllValue) {
			counter = 0;
			for (const activationIdAndHostIdsCombo of this.activationIdAndHostIdsCombos) {
				this.aggregateSelectionForm.controls['select_' + counter].setValue(currCheckAllValue);
				counter++;
			} // for
		} // if
	}

	// *********************************************************
	getUsersName(id: number): string {
		return this.usersService.getUsersName(id);
	}

	// *********************************************************
	canDeleteJournalEntry(journal: Models.JournalEntry): boolean {
		return this.authUser.id === journal.added_by || this.authUser.role === AppConstants.adminUserRole;
	}

	// *********************************************************
	async deleteJournalEntry(journal: Models.JournalEntry) {
		if (confirm('Are you sure you want to delete this journal entry?')) {
			try {
				// this.saving = true;
				await this.adminLogsService.deleteJournal(journal.id);
				this.uiAlertsService.addMsg('The journal entry has been deleted.', 'success', '', false, AppConstants.standardMessageAutoCloseTimeSecs);
				const idx = MiscTools.findIndex(this.journals, journal.id);
				if (idx !== -1) this.journals.splice(idx, 1);
			} catch (e: any) {
				this.uiAlertsService.addMsg(e.message, 'danger', '', false, AppConstants.standardMessageAutoCloseTimeSecs);
			}
		}
	}

	// *********************************************************
	async openUsageReport(metricType: string) {
		if (this.staffMode) {
			let args = '';
			if (this.pageMode === 'activation') {
				args = 'id=' + this.activation.id + '&hostid=';
			} else if (this.pageMode === 'hostid') {
				if (this.filterID !== 0)
					args = 'id=' + this.filterID;
				else
					args = 'id=0';
				args += '&hostid=' + encodeURIComponent(this.hostid);
			} // if
			args += '&metricType=' + encodeURIComponent(metricType);
			if (this.activeProtocolBillingCode !== -1)
				args += '&billingCodeId=' + encodeURIComponent(this.activeProtocolBillingCode);
			if (this.activationIdAndHostIdsCombos && this.activationIdAndHostIdsCombos.length > 0)
				await this.reportsService.runReportViaPost('LicenseUsageReport', args, this.activationIdAndHostIdsCombos);
			else
				await this.reportsService.runReport('LicenseUsageReport', args);
		} else {
			let args = 'key=' + encodeURIComponent(this.userKey.activation_key);
			if (this.pageMode === 'hostid')
				args += '&hostid=' + encodeURIComponent(this.hostid);
			args += '&metricType=' + encodeURIComponent(metricType);
			if (this.activeProtocolBillingCode !== -1)
				args += '&billingCodeId=' + encodeURIComponent(this.activeProtocolBillingCode);
			await this.reportsService.runReport('LicenseUsageReport', args);
		} // if
	}

	// *********************************************************
	workoutUsageBuffers() {
		if (this.pageMode === 'activation') {
			this.byDayMeterBuffer += 40; // make room for report button
			this.byDayProtocolBuffer += 40; // make room for report button

		} else if (this.pageMode === 'hostid') {
			this.byDayMeterBuffer += 40; // make room for report button
			this.byDayProtocolBuffer += 40; // make room for report button

		} else if (this.pageMode === 'aggregate') {

		} // if

		if (this.usedProtocolBillingCodes.length > 0) {
			this.byMonthProtocolBuffer += 40;
		} // if
	}

	// --------------------------------------------------------------------
	// NON STAFF MODE METHODS
	// --------------------------------------------------------------------

	// *********************************************************
	async recover() {
		this.loading = true;
		this.userKey.is_deleted = 0;
		this.showPurge = false;
		this.uiAlertsService.addMsg('The key has been recovered from the trash.', 'info', '', false, AppConstants.standardMessageAutoCloseTimeSecs);
		this.userKey = await this.myKeysService.updateKey(this.userKey);
		await this.myKeysService.getUserKeys(false, true);
		await this.myKeysService.getUserKeys(true, true);
		await this.loadData(true);
	}

	// *********************************************************
	async delete() {
		if (confirm('Are you sure you want to move this key to the trash?')) {
			this.loading = true;
			this.userKey.is_deleted = 1;
			this.uiAlertsService.addMsg('The key has been moved to the trash.', 'info', '', false, AppConstants.standardMessageAutoCloseTimeSecs);
			const result = await this.myKeysService.updateKey(this.userKey);
			await this.myKeysService.getUserKeys(false, true);
			await this.myKeysService.getUserKeys(true, true);
			await this.loadData(true);
		}
	}

	// *********************************************************
	async purge() {
		if (confirm('Are you sure you want to permanently remove this keys from your account?')) {
			const result = await this.myKeysService.purgeKey(this.userKey);
			if (result) {
				this.uiAlertsService.addMsg('The key has removed from your account.', 'info', '', false, AppConstants.standardMessageAutoCloseTimeSecs);
				this.router.navigate(['..'], { relativeTo: this.route });
			} else {
				return false;
			}
		}
	}

	// *********************************************************
	async registerKey() {
		const result = await this.myKeysService.registerKey(this.userKey.activation_key, '');
		if (result) {
			this.userKey = await this.myKeysService.resolveKey(this.userKey.activation_key);
			this.registeredKey = this.userKey.id !== 0;
			this.loadData(true);
		} // if
	}

	// *********************************************************
	async workoutSelfService() {
		this.selfServicePackageId = 0;
		this.selfServiceTemplateId = 0;
		this.showSelfServiceExtendButton = false;
		this.showSelfServiceCreateButton = false;

		// only allow for extending or creating new keys if this key is registered to the user
		if (this.registeredKey) {
			const selfServiceInfo: any = await this.myKeysService.getSelfServiceInfo(this.userKey);

			// console.log(selfServiceInfo);

			if (selfServiceInfo) {
				this.selfServicePackageId = +selfServiceInfo.packageId;
				this.selfServiceTemplateId = +selfServiceInfo.templateId;
				this.showSelfServiceExtendButton = selfServiceInfo.canExtendActivatons;
				this.showSelfServiceCreateButton = selfServiceInfo.canCreateAnother;
			} // if
		} // if
	}

	// *********************************************************
	async generateKeyViaTemplate() {
		try {
			this.loading = true;
			const ret = await this.myKeysService.generateKeyViaTemplate(this.selfServicePackageId, this.selfServiceTemplateId);
			if (ret) {
				this.uiAlertsService.addMsg('Your new key is available.', 'info', '', false, AppConstants.standardMessageAutoCloseTimeSecs);
				this.router.navigate(['/' + AppConstants.urls.mykeys, ret.id]);
			} else {
				this.loading = false;
				this.uiAlertsService.addMsg('There was an error adding your key.', 'error');
			}
		} catch (e: any) {
			this.loading = false;
			this.uiAlertsService.addMsg(e.message, 'error');
		}
	}

	// *********************************************************
	async addActivationsToKey() {
		try {
			this.loading = true;

			const ret = await this.myKeysService.addActivationsToKey(this.userKey);
			if (ret) {
				await this.myKeysService.getUserKeys(false, true);
				await this.myKeysService.getUserKeys(true, true);
				await this.loadData(true);
				this.uiAlertsService.addMsg('The key\'s # of activations has been increased.', 'info', '', false, AppConstants.standardMessageAutoCloseTimeSecs);
			} else {
				this.loading = false;
				this.uiAlertsService.addMsg('There was an error adding your key.', 'error');
			}
		} catch (e: any) {
			this.loading = false;
			this.uiAlertsService.addMsg(e.message, 'error');
		}
	}

	// *********************************************************
	async deleteSnooze(snooze: Models.UserLicenseKeySnooze) {
		if (confirm('Are you sure you want to cancel this?')) {
			try {
				await this.myKeysService.deleteUserSnooze(snooze);
				const idx = MiscTools.findIndex(this.keySnoozes, snooze.id);
				if (idx !== -1)
					this.keySnoozes.splice(idx, 1);

			} catch (e: any) {
				this.uiAlertsService.addMsg(e.message, 'danger', '', false, AppConstants.standardMessageAutoCloseTimeSecs);
			}
		}
	}

	// *********************************************************
	async editKeySnooze(snoozeId: number) {
		this.snoozeErrors = [];
		this.snoozeId = snoozeId;
		this.theSnooze = null;
		if (this.snoozeId && this.snoozeId !== 0) {
			const userSnoozes = await this.myKeysService.getUserSnoozes();
			const idx = MiscTools.findIndex(userSnoozes, this.snoozeId);
			if (idx !== -1) {
				this.theSnooze = userSnoozes[idx];
			} // if
		}

		if (!this.theSnooze) {
			this.theSnooze = new Models.UserLicenseKeySnooze(0, this.authUser.id, this.userKey.id, null, '');
			if (this.snoozeType && this.snoozeType !== ''
				&& MiscTools.findIndexGeneric(AppConstants.keySnoozeTypes, 'value', this.snoozeType) !== -1)
				this.theSnooze.snooze_type = this.snoozeType;
			this.theSnooze.expires_at = MiscTools.dateFromNow(AppConstants.defaultSnoozeDays, 'day');
		} // if

		if (this.theSnooze.snooze_type === '') this.theSnooze.snooze_type = 'all';

		let expiresAtStruct: NgbDateStruct = null;
		if (this.theSnooze.expires_at) {
			const expiresAtDate = new Date(this.theSnooze.expires_at);
			expiresAtStruct = { day: expiresAtDate.getUTCDate(), month: expiresAtDate.getUTCMonth() + 1, year: expiresAtDate.getUTCFullYear() };
		}

		this.snoozeForm = new UntypedFormGroup({
			snooze_type: new UntypedFormControl(this.theSnooze.snooze_type, [Validators.required]),
			expires_at: new UntypedFormControl(expiresAtStruct, [Validators.required])
		});

		UITools.clickButton('open-edit-key-snooze-modal');
	}

	// *********************************************************
	increaseSnoozeExpiry(amount: number, unit = 'day') {
		let theDate: Date = null;

		const now = new Date();

		if (amount === -1 && unit === 'next-month') {
			const daysInMonth = MiscTools.daysInMonth(now.getMonth() + 1, now.getFullYear());
			theDate = new Date(now.getFullYear().toString().padStart(4, '0')
				+ '-' + (now.getMonth() + 1).toString().padStart(2, '0')
				+ '-' + daysInMonth.toString().padStart(2, '0') + ' 00:00:00 UTC');
			theDate = MiscTools.dateIntervalAdd(theDate, 1, 'day');
		} else {
			let expiresDate: Date = null;
			const expiresStruct: NgbDateStruct = this.snoozeForm.value.expires_at;
			if (expiresStruct) {
				expiresDate = new Date(expiresStruct.year + '/' + expiresStruct.month + '/' + expiresStruct.day + ' 00:00:00 UTC');
				if (isNaN(expiresDate.getTime())) expiresDate = null;
			}
			if (expiresDate === null) expiresDate = new Date();

			theDate = MiscTools.dateIntervalAdd(expiresDate, amount, unit);
			// if (this.maxExpiryDate != null && theDate && !isNaN(theDate.getTime()) && theDate.getTime() > this.maxExpiryDate.getTime())
			// 	theDate = new Date(this.maxExpiryDate);
		} // if

		if (theDate && !isNaN(theDate.getTime())) {
			const expiresAt: NgbDateStruct = { day: theDate.getUTCDate(), month: theDate.getUTCMonth() + 1, year: theDate.getUTCFullYear() };
			this.snoozeForm.controls['expires_at' + ''].setValue(expiresAt);
		}
	}

	// *********************************************************
	async saveKeySnooze() {
		this.saving = true;

		this.snoozeErrors = [];

		const expiresStruct: NgbDateStruct = this.snoozeForm.value.expires_at;
		if (expiresStruct) {
			let expiresDate: Date = new Date(expiresStruct.year + '/' + expiresStruct.month + '/' + expiresStruct.day + ' 00:00:00 UTC');
			if (isNaN(expiresDate.getTime())) expiresDate = null;
			if (expiresDate)
				this.theSnooze.expires_at = expiresDate;
		} // if
		this.theSnooze.snooze_type = this.snoozeForm.value.snooze_type.trim();

		const now = new Date();
		if (!this.theSnooze.expires_at)
			this.snoozeErrors.push('You must supply a date.');
		else if (this.theSnooze.expires_at.getTime() < now.getTime())
			this.snoozeErrors.push('The date must be in the future.');

		if (this.snoozeErrors.length > 0) {
			this.uiAlertsService.addMsgs(this.snoozeErrors, 'warning', '', false, AppConstants.standardMessageAutoCloseTimeSecs);
			this.saving = false;
			return;
		} // if

		UITools.clickButton('close-edit-key-snooze-modal');

		try {
			let ret: Models.UserLicenseKeySnooze = null

			if (this.snoozeId && this.snoozeId !== 0)
				ret = await this.myKeysService.updateUserSnooze(this.theSnooze);
			else
				ret = await this.myKeysService.addUserSnooze(this.theSnooze);

			if (ret) {
				const userSnoozes = await this.myKeysService.getUserSnoozes();
				this.keySnoozes = [];
				for (const snooze of userSnoozes)
					if (snooze.user_key_id === this.userKey.id)
						this.keySnoozes.push(snooze);

				this.keySnoozes.sort((a, b) => (a.expires_at > b.expires_at) ? 1 : -1);

				this.saving = false;
			} else {
				this.saving = false;
				this.uiAlertsService.addMsg('Something went wrong,', 'danger', '', false, AppConstants.standardMessageAutoCloseTimeSecs);
			}
		} catch (e: any) {
			this.saving = false;
			this.uiAlertsService.addMsg(e.message, 'danger', '', false, AppConstants.standardMessageAutoCloseTimeSecs);
		}

	}
	// *********************************************************
	async editKeyLabel() {
		// editKeyLabelErrors: string[] = [];
		// editKeyLabelForm: UntypedFormGroup;

		this.editKeyLabelForm = new UntypedFormGroup({
			label: new UntypedFormControl(this.userKey.label, [Validators.maxLength(100)])
		});
		UITools.clickButton('open-edit-key-label-modal');
	}

	// *********************************************************
	async saveKeyLabel() {
		this.saving = true;
		this.userKey.label = this.editKeyLabelForm.value.label.trim();

		UITools.clickButton('close-edit-key-label-modal');
		try {
			const ret = await this.myKeysService.updateKey(this.userKey);
			if (ret) {
				// this.router.navigate([AppConstants.urls.mykeys + '/' + this.licenseKey.id]);
				// this.onCancel();
				this.saving = false;
			} else {
				this.saving = false;
				this.uiAlertsService.addMsg('Something went wrong,', 'danger', '', false, AppConstants.standardMessageAutoCloseTimeSecs);
			}
		} catch (e: any) {
			this.saving = false;
			this.uiAlertsService.addMsg(e.message, 'danger', '', false, AppConstants.standardMessageAutoCloseTimeSecs);
		}
	}

	// *********************************************************
	async setHostIdLabels() {
		this.addHostIdLabelsForm = new UntypedFormGroup({
			pairsList: new UntypedFormControl('', [Validators.required]),
		});
		UITools.clickButton('open-set-hostid-labels-modal');
	}

	// *********************************************************
	async saveHostIdLabels() {
		this.saving = true;

		this.hostids = await this.myKeysService.getUserHostids(true);

		const pairsList = this.addHostIdLabelsForm.value.pairsList;

		const registerErrors = [];

		let numProcessed = 0;
		let numAdded = 0;
		let numUpdated = 0;
		const array = pairsList.match(/[^\r\n]+/g);

		const warnings = [];

		for (const pair of array) {
			if (pair.trim() !== '') {
				numProcessed++;

				let justHostID = '';
				let justLabel = '';
				if (pair.includes(':')) {
					justHostID = TextTools.getLabel(pair.trim()); // everything before the :
					justLabel = TextTools.getValue(pair.trim()); // everything after the :
				} else if (pair.includes(' ')) {
					justHostID = TextTools.getLabel(pair.trim(), ' '); // everything before the space
					justLabel = TextTools.getValue(pair.trim(), ' '); // everything after the space
				} // if

				if (justHostID === '' || justLabel === '') {
					warnings.push('Skipping line "' + pair.trim() + '" need a host ID and label.');
				} else {
					const idx = MiscTools.findIndexGeneric(this.hostids, 'hostid', justHostID);
					try {
						if (idx !== -1) {
							numUpdated++;
						} else {
							numAdded++;
						}
						await this.myKeysService.registerHostid(justHostID, justLabel);
					} catch (e: any) {
						registerErrors.push(e.message);
					}
				}
			} // if
		} // for

		if (warnings.length !== 0)
			this.uiAlertsService.addMsg(warnings.join('\n'), 'info', '', false, AppConstants.standardMessageAutoCloseTimeSecs);

		if (registerErrors.length > 0)
			this.uiAlertsService.addMsg(registerErrors.join('\n'), 'error');

		if (numAdded === 1)
			this.uiAlertsService.addMsg('Registered one host ID.', 'info', '', false, AppConstants.standardMessageAutoCloseTimeSecs);
		else if (numAdded > 1)
			this.uiAlertsService.addMsg('Registered ' + numAdded + ' host IDs.', 'info', '', false, AppConstants.standardMessageAutoCloseTimeSecs);

		if (numUpdated === 1)
			this.uiAlertsService.addMsg('Updated label for one host ID.', 'info', '', false, AppConstants.standardMessageAutoCloseTimeSecs);
		else if (numUpdated > 1)
			this.uiAlertsService.addMsg('Updated label for ' + numUpdated + ' host IDs.', 'info', '', false, AppConstants.standardMessageAutoCloseTimeSecs);

		UITools.clickButton('close-set-hostid-labels-modal');
		this.saving = false;
		if ((numAdded > 0 || numUpdated > 0)) {
			UITools.clickButton('nav-base-tab');
			await this.loadData(true);
		} // if
	}


	// *********************************************************
	async editHostIdLabel() {

		this.hostids = await this.myKeysService.getUserHostids(true);

		const idx = MiscTools.findIndexGeneric(this.hostids, 'hostid', this.hostid);
		let currentLabel = '';
		if (idx !== -1) currentLabel = this.hostids[idx].label;
		this.editHostIdLabelForm = new UntypedFormGroup({
			label: new UntypedFormControl(currentLabel, [Validators.maxLength(255)])
		});
		UITools.clickButton('open-edit-hostid-label-modal');
	}

	// *********************************************************
	async saveHostIdLabel() {
		this.saving = true;

		const newLabel = this.editHostIdLabelForm.value.label.trim();

		UITools.clickButton('close-edit-hostid-label-modal');
		try {
			const ret = await this.myKeysService.registerHostid(this.hostid, newLabel);
			if (ret) {
				// this.router.navigate([AppConstants.urls.mykeys + '/' + this.licenseKey.id]);
				this.saving = false;

				this.label = newLabel;

				UITools.clickButton('nav-base-tab');
				// await this.loadData(true);

			} else {
				this.saving = false;
				this.uiAlertsService.addMsg('Something went wrong,', 'danger', '', false, AppConstants.standardMessageAutoCloseTimeSecs);
			}
		} catch (e: any) {
			this.saving = false;
			this.uiAlertsService.addMsg(e.message, 'danger', '', false, AppConstants.standardMessageAutoCloseTimeSecs);
		}
	}

	// *********************************************************
	// everything after this point should be identical between the staff and non-staff versions
	// *********************************************************

	// *********************************************************
	async selectMeterDayToFocus(product: string, year: number, month: number) {
		if (this.timeMode !== 'month') return;

		UITools.clickButton('open-daily-meter-panel');
		if (this.byDayMeterProduct !== product || this.byDayMeterMonth !== month || this.byDayMeterYear !== year) {
			this.byDayMeterLoading = true;
			this.byDayMeterProduct = product;
			this.byDayMeterMonth = month;
			this.byDayMeterYear = year;
			this.byDayMeterTotal = 0;
			this.byDayMeterAvgAbsChg = 0;
			this.byDayMeterUsage = [];

			if (this.staffMode) {
				if (this.pageMode === 'activation')
					this.byDayMeterUsage = await this.licensingService.getActivationDailyUsageForMonth(this.activation.id, product, year, month);
				else if (this.pageMode === 'hostid')
					this.byDayMeterUsage = await this.licensingService.getHostDailyUsageForMonth(this.hostid, product, year, month, '', this.filterID);
				else if (this.pageMode === 'aggregate')
					this.byDayMeterUsage = await this.licensingService.getAggregateDailyUsage(this.activationIdAndHostIdsCombos, product, year, month, '');
			} else {
				if (this.pageMode === 'activation')
					this.byDayMeterUsage = await this.myKeysService.getActivationDailyUsageForMonth(this.userKey, product, year, month);
				else if (this.pageMode === 'hostid')
					this.byDayMeterUsage = await this.myKeysService.getHostDailyUsageForMonth(this.userKey, this.hostid, product, year, month);
			} // if

			if (this.showNoUseDays) {
				const now = new Date();
				let firstDayOfMonth = 1;
				let lastDayOfMonth = MiscTools.daysInMonth(month, year);

				// adjust if month when key was created...
				if (this.pageMode === 'activation') {
					const createdDate: Date = new Date(this.activation.created_at);
					if (year === createdDate.getUTCFullYear() && month === (createdDate.getUTCMonth() + 1))
						firstDayOfMonth = createdDate.getUTCDate();
				} // if

				// adjust if current month...
				if (year === now.getUTCFullYear() && month === (now.getUTCMonth() + 1))
					lastDayOfMonth = now.getUTCDate() - 1;

				// adjust if last month of reported data...
				if (this.pageMode === 'activation' && this.activation.last_meter_report) {
					const lastReport = new Date(this.activation.last_meter_report);
					if (lastReport && !isNaN(lastReport.getTime())
						&& year === lastReport.getUTCFullYear()
						&& month === (lastReport.getUTCMonth() + 1))
						lastDayOfMonth = lastReport.getUTCDate();
				} // if

				for (let i = firstDayOfMonth; i <= lastDayOfMonth; i++)
					if (MiscTools.findIndexGeneric(this.byDayMeterUsage, 'day', i) === -1)
						this.byDayMeterUsage.push(new Models.LPMeterReportByDay(0, '', product, year, month, i, 0));
			} // if

			this.byDayMeterUsage.sort((a, b) => (a.day > b.day) ? 1 : -1);

			for (const dayRec of this.byDayMeterUsage)
				this.byDayMeterTotal += +dayRec.used;

			if (this.byDayMeterUsage.length > 1) {
				let tempTotal = 0;
				for (let i = 1; i < this.byDayMeterUsage.length; i++)
					if (this.byDayMeterUsage[i - 1].used !== 0)
						tempTotal += Math.abs((this.byDayMeterUsage[i].used - this.byDayMeterUsage[i - 1].used)
							/ this.byDayMeterUsage[i - 1].used);
				this.byDayMeterAvgAbsChg = tempTotal / (this.byDayMeterUsage.length - 1);
			} // if

			this.byDayMeterLoading = false;
		}
	}

	// *********************************************************
	async selectProtocolDayToFocus(product: string, year: number, month: number) {
		if (this.timeMode !== 'month') return;

		let productToFetch = product;
		if (product.startsWith('total')) {
			productToFetch = 'calculated_' + product;
		} // if

		UITools.clickButton('open-daily-protocol-panel');

		if (this.byDayProtocolProduct !== productToFetch || this.byDayProtocolMonth !== month || this.byDayProtocolYear !== year) {
			this.byDayProtocolLoading = true;
			this.byDayProtocolProduct = product;
			this.byDayProtocolMonth = month;
			this.byDayProtocolYear = year;
			this.byDayProtocolTotal = 0;
			this.byDayProtocolAvgAbsChg = 0;
			this.byDayProtocolUsage = [];

			if (this.staffMode) {
				if (this.pageMode === 'activation')
					this.byDayProtocolUsage = await this.licensingService.getActivationDailyUsageForMonth(this.activation.id, productToFetch, year, month, this.protocolBillingCodeToSearch, this.brandFilter === '' ? AppConstants.brandFilterEmptyString : this.brandFilter);
				else if (this.pageMode === 'hostid')
					this.byDayProtocolUsage = await this.licensingService.getHostDailyUsageForMonth(this.hostid, productToFetch, year, month, this.protocolBillingCodeToSearch, this.filterID, this.brandFilter === '' ? AppConstants.brandFilterEmptyString : this.brandFilter);
				else if (this.pageMode === 'aggregate')
					this.byDayProtocolUsage = await this.licensingService.getAggregateDailyUsage(this.activationIdAndHostIdsCombos, productToFetch, year, month, this.protocolBillingCodeToSearch, this.brandFilter === '' ? AppConstants.brandFilterEmptyString : this.brandFilter);
			} else {
				if (this.pageMode === 'activation')
					this.byDayProtocolUsage = await this.myKeysService.getActivationDailyUsageForMonth(this.userKey, productToFetch, year, month, this.protocolBillingCodeToSearch);
				else if (this.pageMode === 'hostid')
					this.byDayProtocolUsage = await this.myKeysService.getHostDailyUsageForMonth(this.userKey, this.hostid, productToFetch, year, month, this.protocolBillingCodeToSearch);
			} // if

			if (this.showNoUseDays) {
				const now = new Date();
				let firstDayOfMonth = 1;
				let lastDayOfMonth = MiscTools.daysInMonth(month, year);

				// adjust if month when key was created...
				if (this.pageMode === 'activation') {
					const createdDate: Date = new Date(this.activation.created_at);
					if (year === createdDate.getUTCFullYear() && month === (createdDate.getUTCMonth() + 1))
						firstDayOfMonth = createdDate.getUTCDate();
				} // if

				// adjust if current month...
				if (year === now.getUTCFullYear() && month === (now.getUTCMonth() + 1))
					lastDayOfMonth = now.getUTCDate() - 1;

				// adjust if last month of reported data...
				if (this.pageMode === 'activation' && this.activation.last_protocol_report) {
					const lastReport = new Date(this.activation.last_protocol_report);
					if (lastReport && !isNaN(lastReport.getTime())
						&& year === lastReport.getUTCFullYear()
						&& month === (lastReport.getUTCMonth() + 1))
						lastDayOfMonth = lastReport.getUTCDate();
				} // if

				for (let i = firstDayOfMonth; i <= lastDayOfMonth; i++)
					if (MiscTools.findIndexGeneric(this.byDayProtocolUsage, 'day', i) === -1)
						this.byDayProtocolUsage.push(new Models.LPMeterReportByDay(0, '', productToFetch, year, month, i, 0));
			} // if

			this.byDayProtocolUsage.sort((a, b) => (a.day > b.day) ? 1 : -1);

			for (const dayRec of this.byDayProtocolUsage)
				this.byDayProtocolTotal += +dayRec.used;

			if (this.byDayProtocolUsage.length > 1) {
				let tempTotal = 0;
				for (let i = 1; i < this.byDayProtocolUsage.length; i++)
					if (this.byDayProtocolUsage[i - 1].used !== 0)
						tempTotal += Math.abs((this.byDayProtocolUsage[i].used - this.byDayProtocolUsage[i - 1].used)
							/ this.byDayProtocolUsage[i - 1].used);
				this.byDayProtocolAvgAbsChg = tempTotal / (this.byDayProtocolUsage.length - 1);
			}

			this.byDayProtocolLoading = false;
		}
	}

	// *********************************************************
	async selectProtocolBillingCodeFilter(id: number) {
		this.protocolBillingCodeToSearch = '';
		if (id === -1) {
			this.activeProtocolBillingCodeLabel = 'All usage data';
			this.activeProtocolBillingCodeShortLabel = '';
		} else if (id === 0) {
			this.activeProtocolBillingCodeLabel = 'Usage data with no billing code set';
			this.activeProtocolBillingCodeShortLabel = 'No billing code';
			this.protocolBillingCodeToSearch = AppConstants.noBillingCodeToken;
		} else {
			const idx = MiscTools.findIndex(this.usedProtocolBillingCodes, id)
			if (idx === -1)
				return this.selectProtocolBillingCodeFilter(-1);

			this.protocolBillingCodeToSearch = this.usedProtocolBillingCodes[idx].billing_code;
			this.activeProtocolBillingCodeShortLabel = this.usedProtocolBillingCodes[idx].billing_code;
			this.activeProtocolBillingCodeLabel = 'Usage data for '
				+ this.makeBillingCodeLabel(this.usedProtocolBillingCodes[idx]);
		} // if

		this.activeProtocolBillingCode = id;

		UITools.clickButton('protocol-billing-codes-panel');

		this.loading = true;

		// new way - do b.c. filtering in front end
		this.byDayMeterProduct = '';
		this.byDayMeterUsage = [];

		this.byDayProtocolProduct = '';
		this.byDayProtocolUsage = [];

		await this.workoutProtocolUsage();
		this.loading = false;
	}

	// *********************************************************
	makeBillingCodeLabel(bc: Models.BillingCode, everything: boolean = true) {
		let label = bc.billing_code + ': ' + bc.label;
		if (!everything) label = bc.label;
		if (everything && this.staffMode && ['activation', 'aggregate', 'hostid'].includes(this.pageMode)) {
			if (!bc.org_name) {
				const bcOrg = this.organizationsService.getOne(bc.zcp_org_id);
				if (bcOrg) {
					bc.org_name = bcOrg.name;
				} else {
					bc.org_name = 'unknown organization - deleted? - ' + bc.zcp_org_id;
				} // if
			} // if
			label += ' (' + bc.org_name + ')';
		} // if
		return label;
	}

	// *********************************************************
	resetMeterUsage() {
		this.monthlyMeterUsage = [];
		this.chunkedMeterUsage = [];

		this.byDayMeterProduct = '';
		this.byDayMeterMonth = 0;
		this.byDayMeterYear = 0;
		this.byDayMeterUsage = [];
		this.byDayMeterTotal = 0;
		this.byDayMeterAvgAbsChg = 0;
	}

	// *********************************************************
	resetProtocolUsage() {
		this.monthlyProtocolUsage = [];
		this.chunkedProtocolUsage = [];
		this.protocolProducts = [];
		this.protocolProductsToShow = [];
		this.protocolProductsVisible = [];
		this.protocolsInfo = [];
		this.protocolTimeChunks = [];
		this.protocolTimeChunksToShow = [];

		this.hasIncomingProtocolData = false;
		this.hasTranscodeProtocolData = false;
		this.hasOutgoingProtocolData = false;

		this.byDayProtocolProduct = '';
		this.byDayProtocolMonth = 0;
		this.byDayProtocolYear = 0;
		this.byDayProtocolUsage = [];
		this.byDayProtocolTotal = 0
		this.byDayProtocolAvgAbsChg = 0;

		this.uniqueProtocolDeviceTypes = [];
		this.uniqueProtocolBrands = [];
		this.uniqueProtocolDeviceTypesAndBrands = [];
		this.brandFilter = AppConstants.brandFilterNoFilter;
	}

	// *********************************************************
	async workoutMeterUsage() {
		this.meterProductsToShow = [];
		this.chunkedMeterUsage = [];
		this.monthlyProjectedAmounts = {};

		this.chunkedMeterUsage = SharedLicenseTools.buildChunkedUsage(this.timeMode, this.monthlyMeterUsage, this.meterProductsToShow, [], [], null, [], -1, '');

		this.meterProductsToShow.sort();
		if (this.pageMode === 'activation')
			this.meterProductsToShow = SharedLicenseTools.sortMeterTypesBasedOnCommercialType(this.activation.commercial_type, this.meterProductsToShow);

		const labelCounts: any = {};
		for (const product of this.meterProductsToShow) {
			const label = SharedLicenseTools.niceProtocol(product, false);
			if (!labelCounts[label]) labelCounts[label] = 0;
			labelCounts[label]++;
			if (labelCounts[label] > 1) this.showMeterTypeExtra = true;
		} // for

		if (this.timeMode === 'month' && this.pageMode === 'activation') {
			for (const record of this.chunkedMeterUsage) {
				this.monthlyProjectedAmounts[record.product + ':' + record.year + ':' + record.month] = SharedLicenseTools.getMeterProjectedForMonth(this.activation, record.product, record.month, record.year);
			} // for
		} // if

		// this.updateMeterChart();
	}

	// *********************************************************
	getUniqueProtocolsWithUsage(usage: Models.LPMeterReportByMonth[], timeChunks: any[], allProtocolsUsed: string[]): string[] {
		const protocols: string[] = [];
		for (const p of allProtocolsUsed) {
			let pTotal: number = 0;

			for (const record of usage) {
				const idx = MiscTools.findIndexGenericDouble(timeChunks, 'year', +record.year, 'month', +record.month);
				if (idx !== -1 && record.product === p && record.used > 0)
					pTotal += +record.used;
			} // for
			if (pTotal > 0)
				protocols.push(p);
		} // for
		return protocols;
	}

	// *********************************************************
	async setBrandFilter(brand: string) {
		this.brandFilter = brand;

		this.byDayProtocolProduct = '';
		this.byDayProtocolMonth = 0;
		this.byDayProtocolYear = 0;
		this.byDayProtocolUsage = [];
		this.byDayProtocolTotal = 0
		this.byDayProtocolAvgAbsChg = 0;

		await this.workoutProtocolUsage();
	}

	// *********************************************************
	async workoutProtocolUsage() {
		this.protocolProducts = [];
		this.protocolProductsToShow = [];
		this.protocolProductsVisible = [];
		this.protocolsInfo = [];
		this.protocolTimeChunks = [];
		this.protocolTimeChunksToShow = [];

		this.hasIncomingProtocolData = false;
		this.hasTranscodeProtocolData = false;
		this.hasOutgoingProtocolData = false;

		this.chunkedProtocolUsage = [];

		if (this.usedProtocolBillingCodes.length === 0 && this.activeProtocolBillingCode === -1) {
			const uniqueCodes: string[] = [];
			this.usedProtocolBillingCodes = [];

			for (const record of this.monthlyProtocolUsage)
				if (record.billing_code != null && record.billing_code !== '' && !uniqueCodes.includes(record.billing_code))
					uniqueCodes.push(record.billing_code);

			// console.log('uniqueCodes', uniqueCodes.length);
			// console.log(uniqueCodes);

			if (uniqueCodes.length > 0) {
				if (this.staffMode && this.pageMode === 'activation') {
					this.usedProtocolBillingCodes = await this.licensingService.getBillingCodes([], this.activation.id);
					// this.usedProtocolBillingCodes = await this.licensingService.getActivationBillingCodes(this.id);
				} else {
					if (this.staffMode)
						this.usedProtocolBillingCodes = await this.licensingService.getBillingCodes(uniqueCodes);
					else
						this.usedProtocolBillingCodes = await this.myKeysService.getBillingCodes(uniqueCodes);
				} // if
			} // if

			this.lotsOfBillingCodes = (this.usedProtocolBillingCodes.length > AppConstants.lotsOfBillingCodesLimit);

			const orgNames: any = {};
			if (this.organization)
				orgNames[this.organization.id] = this.organization.name;

			// console.log('this.usedBillingCodes');
			// console.log(this.usedBillingCodes);
			for (const bc of this.usedProtocolBillingCodes) {
				if (!bc.org_name) {
					if (orgNames[bc.zcp_org_id]) {
						bc.org_name = orgNames[bc.zcp_org_id];
					} else {
						const bcOrg = this.organizationsService.getOne(bc.zcp_org_id);
						if (bcOrg) {
							bc.org_name = bcOrg.name;
						} else {
							bc.org_name = 'unknown organization - deleted? - ' + bc.zcp_org_id;
						} // if
						orgNames[bc.zcp_org_id] = bc.org_name;
					} // if
				} // if
				this.billingCodesByCode[bc.billing_code] = bc;
			} // for

			this.updateFilteredBillingCodeList();
		} // if

		// this.uniqueProtocolDeviceTypes = [];
		// this.uniqueProtocolBrands = [];
		// this.uniqueProtocolDeviceTypesAndBrands = [];

		if (this.uniqueProtocolDeviceTypesAndBrands.length === 0) {
			for (const record of this.monthlyProtocolUsage) {
				if (record.used > 0) {
					let deviceType: number = +record.device_type;
					let brand: string = record.brand;

					if (!this.uniqueProtocolDeviceTypes.includes(deviceType))
						this.uniqueProtocolDeviceTypes.push(deviceType);

					if (!this.uniqueProtocolBrands.includes(brand))
						this.uniqueProtocolBrands.push(brand);

					const idx: number = MiscTools.findIndexGenericDouble(this.uniqueProtocolDeviceTypesAndBrands, 'deviceType', deviceType, 'brand', brand);
					if (idx === -1)
						this.uniqueProtocolDeviceTypesAndBrands.push({ deviceType, brand });
				} // if
			} // for
			this.uniqueProtocolBrands.sort();
			if (this.uniqueProtocolBrands.length > 0 && !this.uniqueProtocolBrands.includes(AppConstants.brandFilterNoFilter))
				this.uniqueProtocolBrands.unshift(AppConstants.brandFilterNoFilter);
		} // if

		const usedSets: number[] = [];

		this.chunkedProtocolUsage = SharedLicenseTools.buildChunkedUsage(this.timeMode, this.monthlyProtocolUsage, this.protocolProducts, this.protocolTimeChunks,
			this.selectedProtocolSets, this.phantomSets, usedSets, this.activeProtocolBillingCode, this.protocolBillingCodeToSearch, true, this.brandFilter);

		for (const product of this.protocolProducts) {
			if (product.includes('transcoded'))
				this.hasTranscodeProtocolData = true;
			else if (product.endsWith('_in'))
				this.hasIncomingProtocolData = true;
			else if (product.endsWith('_out'))
				this.hasOutgoingProtocolData = true;
		} // if

		if (this.hasIncomingProtocolData) this.protocolProducts.push('total_in');
		if (this.hasTranscodeProtocolData) this.protocolProducts.push('total_transcoded');
		if (this.hasOutgoingProtocolData) this.protocolProducts.push('total_out');
		if (this.hasIncomingProtocolData || this.hasOutgoingProtocolData) this.protocolProducts.push('total_inout');
		if (this.protocolProducts.length > 0) this.protocolProducts.push('total');

		let extraProtocols: string[] = [];
		for (let i = 0; i < this.selectedProtocolSets.length; i++) {
			extraProtocols.push('protocol-set:' + this.selectedProtocolSets[i].id);
			// check to see if this is a linked set and if it excludes a billing code...
			// if it does, add an extra protocolProduct to list that shows the set's amount minus the bc's amount
			if (this.pageMode === 'activation' && this.activation && this.activation.protocolSets) {
				const linkedSet: Models.ActivationProtocolSet = MiscTools.pickItem(this.activation.protocolSets, 'set_id', this.selectedProtocolSets[i].id);
				if (linkedSet && linkedSet.exclude_billing_code)
					extraProtocols.push('protocol-set:' + this.selectedProtocolSets[i].id + '-' + linkedSet.exclude_billing_code);
			} // if
		} // for

		// go through each linked set
		// check to see if this is a linked set and if it excludes a billing code...
		// if it does, then go through the time chunks and get the set's total and bc's total and make new chunk
		if (this.pageMode === 'activation' && this.activation && this.activation.protocolSets) {
			for (const linkedSet of this.activation.protocolSets) {
				if (linkedSet.exclude_billing_code) {
					for (const timeChunk of this.protocolTimeChunks) {
						let setAmount: number = 0;
						const idx = MiscTools.findIndexGenericTriple(this.chunkedProtocolUsage, 'product', 'protocol-set:' + linkedSet.set_id, 'year', +timeChunk.year, 'month', +timeChunk.month);
						if (idx !== -1) setAmount = this.chunkedProtocolUsage[idx].used;

						let bcAmount: number = 0;
						const idx2 = MiscTools.findIndexGenericTriple(this.chunkedProtocolUsage, 'product', 'billing-code:' + linkedSet.exclude_billing_code, 'year', +timeChunk.year, 'month', +timeChunk.month);
						if (idx2 !== -1) bcAmount = this.chunkedProtocolUsage[idx2].used;

						this.chunkedProtocolUsage.push(new Models.LPMeterReportByMonth(0, '', 'protocol-set:' + linkedSet.set_id + '-' + linkedSet.exclude_billing_code, +timeChunk.year, +timeChunk.month, setAmount - bcAmount));
					} // for
				} // if
			} // for
		} // if

		if (this.pageMode === 'activation' && this.timeMode === 'month') {
			for (const linkedSet of this.activation.protocolSets) {
				let psetKey: string = linkedSet.set_id + '';
				// check to see if this is a linked set and if it excludes a billing code...
				// if it does, key the percent + overage to the column/row for the pset with the exclude
				if (linkedSet.exclude_billing_code)
					psetKey += '-' + linkedSet.exclude_billing_code;

				if (linkedSet.projected && !isNaN(+linkedSet.projected) && +linkedSet.projected > 0) {
					let totalAmount: number = 0;
					for (const timeChunk of this.protocolTimeChunks) {
						// get the amount for this month
						let amount: number = 0;
						const idx = MiscTools.findIndexGenericTriple(this.chunkedProtocolUsage, 'product', 'protocol-set:' + psetKey, 'year', +timeChunk.year, 'month', +timeChunk.month);
						if (idx !== -1) amount = this.chunkedProtocolUsage[idx].used;

						totalAmount += +amount;

						let percentOfProjected: number = (+(amount / +linkedSet.projected * 100).toFixed(0));
						let overage: number = 0;
						if (amount > linkedSet.projected) overage = amount - +linkedSet.projected;

						this.chunkedProtocolUsage.push(new Models.LPMeterReportByMonth(0, '', 'pset-percent-used:' + psetKey, +timeChunk.year, +timeChunk.month, percentOfProjected));
						this.chunkedProtocolUsage.push(new Models.LPMeterReportByMonth(0, '', 'pset-overage:' + psetKey, +timeChunk.year, +timeChunk.month, overage));
					} // for

					if (totalAmount > 0) {
						if (this.protocolSetsPosition === 'bottom') {
							extraProtocols.push('pset-percent-used:' + psetKey);
							extraProtocols.push('pset-overage:' + psetKey);
						} else {
							extraProtocols.unshift('pset-overage:' + psetKey);
							extraProtocols.unshift('pset-percent-used:' + psetKey);
						} // if
					} // if
				} // if
			} // for
		} // if

		if (this.activeProtocolBillingCode === -1 && this.usedProtocolBillingCodes.length > 0 && !this.lotsOfBillingCodes) {
			extraProtocols.push('billing-code:' + AppConstants.noBillingCodeToken);
			for (const bc of this.usedProtocolBillingCodes)
				extraProtocols.push('billing-code:' + bc.billing_code);
		} // if

		if (this.protocolSetsPosition === 'bottom') {
			for (const p of extraProtocols)
				this.protocolProducts.push(p);
		} else {
			for (let i = extraProtocols.length - 1; i >= 0; i--)
				this.protocolProducts.unshift(extraProtocols[i]);
		} // if

		if (this.timeMode === 'month') {
			if (this.protocolShowAllMonths) {
				this.protocolTimeChunksToShow = this.protocolTimeChunks;
				this.protocolProductsToShow = this.protocolProducts;
			} else {
				if (this.protocolTimeChunks.length > AppConstants.protocolTrafficMonthsToShow) {
					this.protocolTimeChunksToShow = this.protocolTimeChunks.slice(0, AppConstants.protocolTrafficMonthsToShow);
					// this.protocolProductsToShow = this.getUniqueProtocolsWithUsage(this.chunkedProtocolUsage, this.protocolTimeChunksToShow, this.protocolProducts);
					this.protocolProductsToShow = this.protocolProducts;
				} else {
					this.protocolTimeChunksToShow = this.protocolTimeChunks;
					this.protocolProductsToShow = this.protocolProducts;
				} // if
			} // if
		} else {
			this.protocolTimeChunksToShow = this.protocolTimeChunks;
			this.protocolProductsToShow = this.protocolProducts;
		} // if

		if (this.onlyShowTotals)
			this.protocolProductsVisible = this.getTotalProtocols(this.protocolProductsToShow);
		else
			this.protocolProductsVisible = MiscTools.deepClone(this.protocolProductsToShow);
		this.buildProtocolInfo();
	}

	// *********************************************************
	updateFilteredBillingCodeList() {
		if (this.lotsOfBillingCodes && this.billingCodeFilter && this.billingCodeFilter !== '') {
			const tmpList: Models.BillingCode[] = [];
			const matchWords: string[] = TextTools.getMatchWords(this.billingCodeFilter);
			for (const item of this.usedProtocolBillingCodes) {
				let match: boolean = true;
				if (matchWords.length > 0) {
					let matches: number = 0;
					for (const w of matchWords) {
						if (item.billing_code.toLowerCase().includes(w)
							|| (item.label && item.label.toLowerCase().includes(w))
							|| (item.org_name && item.org_name.toLowerCase().includes(w))
						)
							matches++;
					} // for
					if (matches !== matchWords.length) match = false;
				} // if
				if (match) tmpList.push(item);
			} // for
			this.usedProtocolBillingCodesToShow = tmpList;
		} else {
			this.usedProtocolBillingCodesToShow = MiscTools.deepClone(this.usedProtocolBillingCodes);
		}
	} // updateFilteredBillingCodeList

	// ------------------------------------------------------------------------
	resetBilllingCodeFilter() {
		this.billingCodeFilter = '';
		this.updateFilteredBillingCodeList();
	} // resetBilllingCodeFilter

	// ------------------------------------------------------------------------
	billlingCodeFilterChangeHandler() {
		if (this.bcFilterChangeTimeOut) clearTimeout(this.bcFilterChangeTimeOut);
		const textFilter: string = document.getElementById('bcTextFilter') ? document.getElementById('bcTextFilter')['value'] : '';
		// console.log('textFilterChangeHandler - ' + textFilter);
		if (!textFilter)
			this.updateBilllingCodeFilter();
		else
			this.bcFilterChangeTimeOut = setTimeout(this.updateBilllingCodeFilter.bind(this), this.bcTimeoutMS);
	} // billlingCodeFilterChangeHandler

	// ------------------------------------------------------------------------
	updateBilllingCodeFilter() {
		const textFilter: string = document.getElementById('bcTextFilter') ? document.getElementById('bcTextFilter')['value'] : '';
		// console.log('updateTextFilter - ' + textFilter);
		this.billingCodeFilter = textFilter;
		this.updateFilteredBillingCodeList();
	} // updateBilllingCodeFilter

	// *********************************************************
	toggleBillingCodeData(billingCode: string) {
		const idx2: number = this.protocolProductsVisible.indexOf('billing-code:' + billingCode)
		if (idx2 !== -1) {
			this.protocolProductsVisible.splice(idx2, 1);
		} else {
			if (this.protocolSetsPosition === 'bottom') {
				this.protocolProductsVisible.push('billing-code:' + billingCode);
			} else {
				this.protocolProductsVisible.unshift('billing-code:' + billingCode);
			} // if
		} // if
		this.buildProtocolInfo();
	}

	// *********************************************************
	buildProtocolInfo() {
		this.protocolsInfo = [];
		for (const p of this.protocolProductsVisible) {
			this.protocolsInfo.push({
				protocol: p,
				protocolClass: this.productClass(p),
				niceProtocolLong: this.niceProtocol(p, false),
				niceProtocolShort: this.niceProtocol(p, true),
				superScript: this.getProtocolSuperScripts(p, 'protocol-data'),
				superScriptClass: !p.startsWith('protocol-set') ? 'text-warning' : '',
				popoverLines: this.getProtocolPopoverLines(p, 'protocol-data').join('\n')
			});
		} // for
	}

	// *********************************************************
	async changeChartDirection() {
		this.newToOld = !this.newToOld;
		this.saveSettings();
		await this.updateMeterChart();
		await this.updateProtocolChart(this.protocolChartProduct, true);
	} // 

	// *********************************************************
	async changeIncludeCurrentMonth() {
		this.includeCurrentMonth = !this.includeCurrentMonth;
		this.saveSettings();
		await this.updateMeterChart();
		await this.updateProtocolChart(this.protocolChartProduct, true);
	} // 

	// *********************************************************
	async updateMeterChart() {
		UITools.clickButton('open-meters-chart-modal');

		this.showMeterChart = false;
		this.meterChartLabel = '';
		if (this.timeMode !== 'month') return;

		try {
			let minMonth: any = null;
			let maxMonth: any = null;

			for (const product of this.meterProductsToShow) {
				// const usage: Models.LPMeterReportByMonth[] = this.pullUsageForMeterProduct(product);
				for (const u of this.pullUsageForMeterProduct(product)) {
					// console.log(product + ' ' + u.month + ' ' + u.year + ' ' + u.used);
					if (!minMonth || +u.year < +minMonth.year || (+u.year === +minMonth.year && +u.month < +minMonth.year))
						minMonth = { year: +u.year, month: +u.month };
					if (!maxMonth || +u.year > +maxMonth.year || (+u.year === +maxMonth.year && +u.month > +maxMonth.year))
						maxMonth = { year: +u.year, month: +u.month };
				}
			} // for

			// console.log('minMonth ' + JSON.stringify(minMonth));
			// console.log('maxMonth ' + JSON.stringify(maxMonth));
			if (!minMonth || !maxMonth) return;

			// start at maxMonth and keep going back until 12 months or after minMonth
			const graphMonths: any = [];
			const graphMax: Date = new Date(maxMonth.year + '/' + +maxMonth.month.toString().padStart(2, '0') + '/01 UTC');
			const graphMin: Date = new Date(minMonth.year + '/' + +minMonth.month.toString().padStart(2, '0') + '/01 UTC');
			let counter: Date = new Date(graphMax);
			const now: Date = new Date();
			while (counter.getTime() >= graphMin.getTime() && graphMonths.length < this.maxMonthsForCharts) {
				if (this.includeCurrentMonth || now.getUTCFullYear() !== counter.getUTCFullYear() || now.getUTCMonth() !== counter.getUTCMonth())
					graphMonths.push({ year: counter.getUTCFullYear(), month: counter.getUTCMonth() + 1 });
				counter = MiscTools.dateIntervalAdd(counter, -1, 'month');
			} // while

			if (!this.newToOld)
				graphMonths.reverse();

			// console.log('graphMonths');
			// console.log(JSON.stringify(graphMonths));
			if (graphMonths.length <= 1) {
				this.meterChartLabel = '<span class="fw-bold">Meter Data</span> Usage from <span class="fw-bold">'
					+ '</span> - not enough data points';
			} else {
				let annotations: any = null;

				this.meterChartLabel = '<span class="fw-bold">Meter Data</span> Usage from <span class="fw-bold">'
					+ AppConstants.months[graphMonths[0].month - 1] + ' ' + graphMonths[0].year
					+ '</span> to <span class="fw-bold">' + AppConstants.months[graphMonths[graphMonths.length - 1].month - 1] + ' ' + graphMonths[graphMonths.length - 1].year
					+ '</span>';
				this.meterChartData.datasets = [];
				let pCount: number = 0;
				for (const product of this.meterProductsToShow) {
					const usage: Models.LPMeterReportByMonth[] = this.pullUsageForMeterProduct(product);
					const usageAmounts: number[] = [];
					for (const graphMonth of graphMonths) {
						const idx = MiscTools.findIndexGenericTriple(this.chunkedMeterUsage, 'product', product, 'year', +graphMonth.year, 'month', +graphMonth.month);
						let usage: number = 0;
						if (idx !== -1) usage = this.chunkedMeterUsage[idx].used;
						usageAmounts.push(usage);
					} // if

					const lineColor: string = (pCount < this.lineColors.length) ? this.lineColors[pCount] : this.lineColors[this.lineColors.length - 1];

					this.meterChartData.datasets.push({
						data: usageAmounts,
						label: AppConstants.meterTypeObjects[product] ? AppConstants.meterTypeObjects[product].label : product,
						backgroundColor: 'rgba(148,159,177,0.2)',
						borderColor: lineColor,
						pointBackgroundColor: lineColor,
						pointBorderColor: lineColor,
						pointHoverBackgroundColor: '#fff',
						pointHoverBorderColor: 'rgba(148,159,177,0.8)',
						fill: '',
						pointRadius: 5
					});

					// TBD if there's only one active meter of this type
					// AND it has a a max limit that's not 9 ZB
					// or it has a projected limit
					// add line...


					pCount++;
				} // for

				const graphLabels: string[] = [];
				for (const graphMonth of graphMonths) {
					graphLabels.push(AppConstants.months[graphMonth.month - 1].substring(0, 3) + ' ' + graphMonth.year);
				} // if

				this.meterChartData.labels = graphLabels;
				this.meterChartOptions.plugins.legend.display = (this.meterProductsToShow.length > 1);

				if (annotations)
					this.meterChartOptions.plugins['annotation'] = annotations;
				else
					this.meterChartOptions.plugins['annotation'] = {};

				await MiscTools.delay(100);
				this.showMeterChart = true;
				// await MiscTools.delay(200);
				// if (this.meterChart) this.meterChart.update();
			} // if
		} catch (e: any) {
			console.log('There was an issue rendering the meter chart');
			console.log(e);
		}
	} //

	// *********************************************************
	async updateProtocolChart(product: string, scroll: boolean = false) {
		UITools.clickButton('open-protocol-chart-modal');

		this.showProtocolChart = false;
		this.protocolChartLabel = '';

		// console.log('------------------');
		// console.log('product 1');
		// console.log(product);

		if (!product) {
			if (this.linkedProtocolSets.length > 0)
				this.protocolChartProduct = 'protocol-set:' + this.linkedProtocolSets[0].id;
			else
				this.protocolChartProduct = 'total';
		} else {
			this.protocolChartProduct = product;
		} // if

		// this.protocolChartProduct = product ? product : 'total';
		// console.log('protocolChartProduct 2');
		// console.log(this.protocolChartProduct);

		if (this.timeMode !== 'month') return;

		try {
			if (!this.protocolProductsVisible.includes(this.protocolChartProduct))
				this.protocolChartProduct = 'total';

			// console.log('protocolChartProduct 3');
			// console.log(this.protocolChartProduct);

			let minMonth: any = null;
			let maxMonth: any = null;

			const usage: Models.LPMeterReportByMonth[] = [];
			for (const u of this.chunkedProtocolUsage) {
				if (u.product === this.protocolChartProduct) {
					// console.log(product + ' ' + u.month + ' ' + u.year + ' ' + u.used);
					if (!minMonth || +u.year < +minMonth.year || (+u.year === +minMonth.year && +u.month < +minMonth.year))
						minMonth = { year: +u.year, month: +u.month };
					if (!maxMonth || +u.year > +maxMonth.year || (+u.year === +maxMonth.year && +u.month > +maxMonth.year))
						maxMonth = { year: +u.year, month: +u.month };
					usage.push(u);
				} // if
			} // for

			if (!minMonth || !maxMonth) return;

			// console.log('protocol minMonth ' + JSON.stringify(minMonth));
			// console.log('protocol maxMonth ' + JSON.stringify(maxMonth));

			// start at maxMonth and keep going back until 12 months or after minMonth
			const graphMonths: any = [];
			const graphMax: Date = new Date(maxMonth.year + '/' + +maxMonth.month.toString().padStart(2, '0') + '/01 UTC');
			const graphMin: Date = new Date(minMonth.year + '/' + +minMonth.month.toString().padStart(2, '0') + '/01 UTC');
			let counter: Date = new Date(graphMax);

			const now: Date = new Date();
			while (counter.getTime() >= graphMin.getTime() && graphMonths.length < this.maxMonthsForCharts) {
				if (this.includeCurrentMonth || now.getUTCFullYear() !== counter.getUTCFullYear() || now.getUTCMonth() !== counter.getUTCMonth())
					graphMonths.push({ year: counter.getUTCFullYear(), month: counter.getUTCMonth() + 1 });
				counter = MiscTools.dateIntervalAdd(counter, -1, 'month');
			} // while

			if (!this.newToOld)
				graphMonths.reverse();

			// console.log('graphMonths');
			// console.log(JSON.stringify(graphMonths));

			if (graphMonths.length <= 1) {
				this.protocolChartLabel = '<span class="fw-bold">' + this.niceProtocol(this.protocolChartProduct, false)
					+ '</span> - not enough data points';
			} else {
				this.protocolChartLabel = '<span class="fw-bold">' + this.niceProtocol(this.protocolChartProduct, false)
					+ '</span> Usage from <span class="fw-bold">'
					+ AppConstants.months[graphMonths[0].month - 1] + ' ' + graphMonths[0].year
					+ '</span> to <span class="fw-bold">' + AppConstants.months[graphMonths[graphMonths.length - 1].month - 1] + ' ' + graphMonths[graphMonths.length - 1].year
					+ '</span>';
				this.protocolChartData.datasets = [];

				const usageAmounts: number[] = [];
				for (const graphMonth of graphMonths) {
					const idx = MiscTools.findIndexGenericTriple(this.chunkedProtocolUsage, 'product', this.protocolChartProduct, 'year', +graphMonth.year, 'month', +graphMonth.month);
					let usage: number = 0;
					if (idx !== -1) usage = this.chunkedProtocolUsage[idx].used;
					usageAmounts.push(usage);
				} // if

				this.protocolChartData.datasets.push({
					data: usageAmounts,
					label: this.niceProtocol(this.protocolChartProduct, false),
					backgroundColor: 'rgba(148,159,177,0.2)',
					borderColor: this.lineColors[0],
					pointBackgroundColor: this.lineColors[0],
					pointBorderColor: this.lineColors[0],
					pointHoverBackgroundColor: '#fff',
					pointHoverBorderColor: 'rgba(148,159,177,0.8)',
					fill: '',
					pointRadius: 5
				});

				const graphLabels: string[] = [];
				for (const graphMonth of graphMonths) {
					graphLabels.push(AppConstants.months[graphMonth.month - 1].substring(0, 3) + ' ' + graphMonth.year);
				} // if

				this.protocolChartData.labels = graphLabels;

				let annotations: any = null;
				if (this.activation && this.protocolChartProduct.startsWith('protocol-set:')) {
					const theSet: Models.ProtocolSet = MiscTools.pickItem(this.linkedProtocolSets, 'id', +TextTools.getValue(this.protocolChartProduct));
					if (theSet && !isNaN(+theSet['projected']) && +theSet['projected'] > 0) {

						annotations = {
							annotations: {
								line1: { // Unique ID for your line
									display: true,
									type: 'line',
									yMin: +theSet['projected'],
									yMax: +theSet['projected'],
									borderColor: 'red',
									borderWidth: 2,
									borderDash: [5, 10]
									// Optional: Add a label
									// label: {
									// 	content: 'Threshold',
									// 	enabled: true,
									// 	position: 'end'
									// }
								}
							}
						};
					} // if
				}

				if (annotations)
					this.protocolChartOptions.plugins['annotation'] = annotations;
				else
					this.protocolChartOptions.plugins['annotation'] = {};

				// await MiscTools.delay(200);
				await MiscTools.delay(100);
				this.showProtocolChart = true;

				if (scroll) {
					await MiscTools.delay(100);
					UITools.scrollToSpot('protocolChart')
				}
			} // if
		} catch (e: any) {
			console.log('There was an issue rendering the protocol chart');
			console.log(e);
		}
	}

	// *********************************************************
	pullUsageForMeterProduct(product: string) {
		const usage: Models.LPMeterReportByMonth[] = [];
		for (const record of this.chunkedMeterUsage)
			if (record.product === product && record.used > 0)
				usage.push(record);
		return usage;
	}

	// *********************************************************
	getUsageForProtocolForTimeChunk(product: string, year: number, month: number) {
		const idx = MiscTools.findIndexGenericTriple(this.chunkedProtocolUsage, 'product', product, 'year', year, 'month', month);
		if (idx !== -1)
			return this.chunkedProtocolUsage[idx].used;
		return 0;
	}

	// *********************************************************
	niceKeyType(activation: Models.LPActivation) {
		return (activation.type);
	}

	// *********************************************************
	toggleAxis() {
		this.rotateAxis = !this.rotateAxis;
		this.saveSettings();
	}

	// *********************************************************
	toggleShowNoUseDays() {
		this.showNoUseDays = !this.showNoUseDays;
		this.saveSettings();
	}

	// *********************************************************
	getDayToDayRatio(metricType: string, idx: number): number {
		if (idx > 0)
			if (metricType === 'meter-data' && this.byDayMeterUsage[idx - 1].used !== 0)
				return (this.byDayMeterUsage[idx].used - this.byDayMeterUsage[idx - 1].used)
					/ this.byDayMeterUsage[idx - 1].used;
			else if (metricType === 'protocol-data' && this.byDayProtocolUsage[idx - 1].used !== 0)
				return (this.byDayProtocolUsage[idx].used - this.byDayProtocolUsage[idx - 1].used)
					/ this.byDayProtocolUsage[idx - 1].used;
		return 0;
	}

	// *********************************************************
	dailyRate(sizeMB: number) {
		if (sizeMB == null) return 0;
		const dailyRate = (sizeMB * 8) / (24 * 60 * 60);
		return dailyRate;
		// return Math.max(dailyRate, 0.1).toFixed(1);
	}

	// *********************************************************
	dayLabel(record: Models.LPMeterReportByDay) {
		const theDate: Date = new Date(record.year + '/' + record.month + '/' + record.day + ' 00:00:00 UTC');
		return TextTools.formatDate(theDate, 'EEE d', '', 'UTC');
		// return record.day + ' - ' + TextTools.acronym(AppConstants.daysOfWeek[theDate.getUTCDay()]);
	}

	// *********************************************************
	productClass(product: string): string {
		let classes = 'border';
		// if (product.startsWith('total'))
		// 	classes += ' fw-bold';

		if (product.startsWith('billing-code:'))
			classes += ' cp-table-orange';
		else if (product.startsWith('protocol-set:') || product.startsWith('pset-percent-used:') || product.startsWith('pset-overage:'))
			classes += ' table-warning';
		else if (product.includes('transcoded'))
			classes += ' table-secondary';
		else if (product.endsWith('_in') || product.endsWith('_in' + AppConstants.timeProtocolSuffix))
			classes += ' table-primary';
		else if (product.endsWith('_out') || product.endsWith('_out' + AppConstants.timeProtocolSuffix))
			classes += ' table-info';
		else if (['total', 'total' + AppConstants.timeProtocolSuffix, 'total_inout', 'total_inout' + AppConstants.timeProtocolSuffix].includes(product))
			classes += ' table-success';

		// 1909694129

		return classes;
	}

	// *********************************************************
	hasMeterOfCertainType(meterProduct: string, resets: string) {
		const now = new Date();

		for (const meter of this.activation.meters) {
			let starts = new Date(now);
			if (meter.starts_at) starts = new Date(meter.starts_at);
			const exp = new Date(meter.expires_at);
			if (meter.product === meterProduct && meter.resets === resets
				&& meter.enabled === 1 && exp.getTime() > now.getTime() && starts.getTime() <= now.getTime()) {
				return true;
			} // if
		} // for

		return false;
	}

	// *********************************************************
	niceProtocol(protocol: string, useShort: boolean = true) {
		let name = protocol;

		if (protocol.startsWith('billing-code:')) {
			if (protocol === 'billing-code:' + AppConstants.noBillingCodeToken) {
				name = 'No Code';

			} else if (useShort) {
				if (this.billingCodeDisplay === 'label')
					name = this.makeBillingCodeLabel(this.billingCodesByCode[TextTools.getValue(protocol)], false);
				else
					name = TextTools.getValue(protocol);
			} else {
				name = 'Billing Code: ' + TextTools.getValue(protocol);
			} // if

		} else if (protocol.startsWith('protocol-set:') || protocol.startsWith('pset-percent-used:') || protocol.startsWith('pset-overage:')) {
			let setId: number = 0;
			let excludeBillingCode: string = '';
			const idPart: string = TextTools.getValue(protocol);
			if (!isNaN(+idPart)) {
				setId = +idPart;
			} else {
				setId = +TextTools.getLabel(idPart, '-');
				excludeBillingCode = TextTools.getValue(idPart, '-');
			} // if

			const idx = MiscTools.findIndex(this.availableSets, setId);
			if (idx !== -1)
				name = this.availableSets[idx].name;

			if (!useShort) name = 'Protocol Set: ' + name;

			if (protocol.startsWith('pset-percent-used:'))
				name = '% of Projected for ' + name;
			else if (protocol.startsWith('pset-overage:'))
				name = 'Overage for ' + name;

			if (useShort) name = TextTools.chopString(name, 30, '...');

			if (excludeBillingCode) name += ' excl. ' + excludeBillingCode;
		} else {
			name = SharedLicenseTools.niceProtocol(protocol, useShort);
		} // if

		if (useShort)
			return name.replace(/\s/g, '\n');
		else
			return name;
	} // 

	// *********************************************************
	showDataValue(amt: number, product: string = '') {
		if (!amt || Math.abs(amt) < 0.1) return '0';

		const isNeg: boolean = amt < 0;
		const absAmt: number = Math.abs(amt);

		let ret: string = '';
		// limit decimal places
		if (this.showRawData)
			ret = TextTools.formatNumber(+Math.max(absAmt, 0.1).toFixed(1));
		else
			ret = TextTools.formattedMB(absAmt);

		if (isNeg)
			return ('-' + ret);
		else
			return (ret);

	}

	// *********************************************************
	makeExpDataTip(product: string, ym: any, amt: number, canCopy: boolean = true, canClick: boolean = false) {
		if (amt == null || isNaN(amt) || amt === 0) return '';
		let toolTip = this.niceProtocol(product, false)
			+ ' - ' + this.makeTimeChunkHeader(ym.month, ym.year, false)
			+ ' : ';

		if (!product.startsWith('pset-percent-used:'))
			toolTip += this.makeDataTip(amt, canCopy, canClick);

		return toolTip;
	}

	// *********************************************************
	makeDataTip(amt: number, canCopy: boolean = true, canClick: boolean = false) {
		if (amt == null || isNaN(amt) || amt === 0) return '';
		let toolTip = TextTools.formatNumber(Math.floor(amt)) + ' megabytes';

		if (canCopy) toolTip += ' - click to copy'

		if (canClick && this.timeMode === 'month')
			if (canCopy)
				toolTip += ' and expand'
			else
				toolTip += ' - click to expand'

		return toolTip;
	}

	// *********************************************************
	toggleShowRaw() {
		this.showRawData = !this.showRawData;
		this.saveSettings();
	}

	// *********************************************************
	toggleTotalsOnly() {
		this.onlyShowTotals = !this.onlyShowTotals;
		this.saveSettings();

		if (this.onlyShowTotals)
			this.protocolProductsVisible = this.getTotalProtocols(this.protocolProductsToShow);
		else
			this.protocolProductsVisible = this.protocolProductsToShow;
		this.buildProtocolInfo();
	}

	// *********************************************************
	getTotalProtocols(protocols: string[]) {
		const justTotals: string[] = [];
		for (const protocol of protocols)
			if (['total', 'total_inout', 'total_in', 'total_transcoded', 'total_out',].includes(protocol)
				|| protocol.startsWith('billing-code:') || protocol.startsWith('protocol-set:')
				|| protocol.startsWith('pset-percent-used:') || protocol.startsWith('pset-overage:')
			)
				justTotals.push(protocol);
		return justTotals;
	}

	// *********************************************************
	changeTimeMode(mode: string) {
		const idx = MiscTools.findIndexGeneric(this.timeModes, 'value', mode);
		if (idx !== -1) {
			this.byDayMeterProduct = '';
			this.byDayMeterUsage = [];

			this.byDayProtocolProduct = '';
			this.byDayProtocolUsage = [];

			this.timeMode = mode;
			this.timeModeLabel = this.timeModes[idx].label;

			this.workoutMeterUsage();
			this.workoutProtocolUsage();
		} // if
	}

	// *********************************************************
	makeTimeChunkHeader(m: number, y: number, withBreak: boolean) {
		let header = '';

		if (this.timeMode === 'month')
			header = TextTools.getMonthName(m, true) + ' ' + y;
		else if (this.timeMode === 'quarter')
			header = y + ' Q' + m;
		else if (this.timeMode === 'year')
			header = y + '';

		if (withBreak) header = header.replace(/\s/g, '\n');

		return header;
	}

	// *********************************************************
	getProtocolSuperScripts(product: string, dataType: string) {
		if (product.startsWith('protocol-set:')) {
			let setId: number = 0;
			const idPart: string = TextTools.getValue(product);
			if (!isNaN(+idPart)) {
				setId = +idPart;
			} else {
				setId = +TextTools.getLabel(idPart, '-');
			} // if

			if (dataType === 'protocol-data') {
				const idx = MiscTools.findIndex(this.selectedProtocolSets, setId);
				if (idx !== -1)
					return '#' + (idx + 1);
			} // if
		} else if (!product.startsWith('total')) {
			let setsToCheck: Models.ProtocolSet[] = [];
			if (dataType === 'protocol-data')
				setsToCheck = this.selectedProtocolSets;

			const nums: number[] = [];
			let counter = 0;
			for (const protocolSet of setsToCheck) {
				counter++;
				if (protocolSet.protocolsArr.includes(product))
					nums.push(counter);
			} // for

			if (nums.length > 0)
				return '#' + nums.join(' #');
		} // if
		return '';
	}

	// --------------------------------------------------------------------
	workoutMonthsProjected(record: Models.LPMeterReportByMonth) {
		let amt: number = 0;
		if (this.monthlyProjectedAmounts[record.product + ':' + record.year + ':' + record.month])
			amt = +this.monthlyProjectedAmounts[record.product + ':' + record.year + ':' + record.month];
		return amt
	} // 

	// --------------------------------------------------------------------
	workoutMonthsOverage(record: Models.LPMeterReportByMonth) {
		const proj: number = this.workoutMonthsProjected(record);
		if (proj !== 0 && record.used > proj) return record.used - proj;
		return 0;
	} // 

	// *********************************************************
	getProtocolPopoverLines(product: string, dataType: string): string[] {
		let lines: string[] = [];

		let setsToCheck: Models.ProtocolSet[] = [];
		if (dataType === 'protocol-data')
			setsToCheck = this.selectedProtocolSets;

		if (product.startsWith('billing-code:')) {
			if (product === 'billing-code:' + AppConstants.noBillingCodeToken) {
				lines.push('Data reported that either has no billing code set OR the billing code\'s authentication code is invalid.');
			} else {
				lines.push('All data reported against this billing code.');
				if (this.billingCodesByCode[TextTools.getValue(product)])
					lines.push(this.makeBillingCodeLabel(this.billingCodesByCode[TextTools.getValue(product)]));
			}
		} else if (product.startsWith('protocol-set:')) {
			let setId: number = 0;
			let excludeBillingCode: string = '';
			const idPart: string = TextTools.getValue(product);
			if (!isNaN(+idPart)) {
				setId = +idPart;
			} else {
				setId = +TextTools.getLabel(idPart, '-');
				excludeBillingCode = TextTools.getValue(idPart, '-');
			} // if

			const idx = MiscTools.findIndex(setsToCheck, setId);
			if (idx !== -1) {
				lines.push('Protocol Set #' + (idx + 1));
				lines = lines.concat(SharedLicenseTools.getProtocolSetPopoverLines(setsToCheck[idx], false));
				if (excludeBillingCode)
					lines.push('<span class="fw-bold">Amounts exclude data from Billing Code - ' + excludeBillingCode + '</span>');
			} // if

		} else if (product.startsWith('pset-percent-used:') || product.startsWith('pset-overage:')) {
			let setId: number = 0;
			let excludeBillingCode: string = '';
			const idPart: string = TextTools.getValue(product);
			if (!isNaN(+idPart)) {
				setId = +idPart;
			} else {
				setId = +TextTools.getLabel(idPart, '-');
				excludeBillingCode = TextTools.getValue(idPart, '-');
			} // if

			if (this.pageMode === 'activation' && this.activation && this.activation.protocolSets) {
				const linkedSet: Models.ActivationProtocolSet = MiscTools.pickItem(this.activation.protocolSets, 'set_id', setId);
				if (linkedSet && linkedSet.projected && linkedSet.projected > 0)
					lines.push('Projected Limit: ' + TextTools.formattedMB(linkedSet.projected));
				else
					lines.push('Projected Limit: ???');
				if (excludeBillingCode)
					lines.push('<span class="fw-bold">Amounts exclude data from Billing Code - ' + excludeBillingCode + '</span>');
			} // if

		} else if (product.startsWith('total')) {
			lines.push('Calculated Total');

		} else {
			if (product.endsWith(AppConstants.timeProtocolSuffix))
				if (product.startsWith('mediaconnect_'))
					lines.push('MediaConnect related usage amount (time based) reported by ZEN Master');
				else if (product.startsWith('zm_'))
					lines.push('Non-MediaConnect related usage amount (time based) reported by ZEN Master');
				else
					lines.push('Usage amount (time based) reported by Zixi Broadcaster/ZEC');
			else
				if (product.startsWith('mediaconnect_'))
					lines.push('MediaConnect related usage amount (data based) reported by ZEN Master');
				else if (product.startsWith('zm_'))
					lines.push('Non-MediaConnect related usage amount (data based) reported by ZEN Master');
				else
					lines.push('Usage amount (data based) reported by Zixi Broadcaster/ZEC');

			let counter = 0;
			for (const protocolSet of setsToCheck) {
				counter++;
				if (protocolSet.protocolsArr.includes(product))
					lines.push('Included in Protocol Set # ' + counter + ': ' + protocolSet.name);
			} // for
		} // if

		return lines;
	}

	// *********************************************************
	async toggleSetsPosition() {
		if (this.protocolSetsPosition === 'top')
			this.protocolSetsPosition = 'bottom';
		else
			this.protocolSetsPosition = 'top';
		this.saveSettings();
		await this.workoutProtocolUsage();
	}

	// *********************************************************
	async toggleBillingCodeDisplay() {
		if (this.billingCodeDisplay === 'code')
			this.billingCodeDisplay = 'label';
		else
			this.billingCodeDisplay = 'code';
		this.saveSettings();
		this.buildProtocolInfo();
	}

	// *********************************************************
	filterOutSelected(selectedSets: Models.ProtocolSet[]) {
		const filtered: Models.ProtocolSet[] = [];
		for (const protocolSet of this.availableSets)
			if (MiscTools.findIndex(selectedSets, protocolSet.id) === -1)
				filtered.push(protocolSet);
		return filtered;
	}

	// *********************************************************
	async toggleSessionSet(setId: number, dataType: string) {
		const idx1 = MiscTools.findIndex(this.availableSets, setId);
		if (idx1 !== -1) {
			if (dataType === 'protocol-data') {
				const idx2 = MiscTools.findIndex(this.sessionProtocolSets, setId);
				if (idx2 !== -1)
					this.sessionProtocolSets.splice(idx2, 1);
				else
					this.sessionProtocolSets.push(this.availableSets[idx1]);

				// save session protocol sets for this key
				localStorage.setItem('CLEAN.' + this.storageKeyPrefix + 'sessionProtocolSets.' + this.sessionSetIdentifier, JSON.stringify(this.pullSetIds(this.sessionProtocolSets)));

				this.selectedProtocolSets = this.linkedProtocolSets.concat(this.sessionProtocolSets);

				await this.workoutProtocolUsage();
			} // if
		} // if
	} //

	// *********************************************************
	pullSetIds(sets: Models.ProtocolSet[]) {
		const ids: number[] = [];
		for (const protocolSet of sets)
			ids.push(protocolSet.id);
		return ids;
	}

	// *********************************************************
	async linkOfflineLicenseToKey() {

		const linkKey: string = this.linkOfflineForm.value.linkKey;

		// latestID: number = 0;
		// showLinkOfflineLicense = false;
		// linkOfflineForm: FormGroup;
		UITools.clickButton('close-link-offline-modal');

		try {
			const response: Models.LPActivation = await this.licensingService.linkLegacyLicenseToKey(this.latestID, linkKey);

			this.router.navigate(['/' + AppConstants.urls.licensing, 'activation', response.id]);

		} catch (e: any) {
			this.uiAlertsService.addMsg(e.message, 'danger', '', false, AppConstants.standardMessageAutoCloseTimeSecs);
		}
	}
	// --------------------------------------------------------------------
	copyToClipboardAlert(item: string = '') {
		this.uiAlertsService.copyToClipboardAlert(item);
	}

	// --------------------------------------------------------------------
	routeToKey(keyId: number) {
		const tabs = document.getElementsByClassName("nav-item nav-link active");
		let currentTab: string = '';
		if (tabs && tabs[0]) currentTab = tabs[0].id;

		if (currentTab && currentTab !== '')
			this.router.navigate(['/' + AppConstants.urls.licensing, 'activation', keyId], { queryParams: { tabID: currentTab } });
		else
			this.router.navigate(['/' + AppConstants.urls.licensing, 'activation', keyId]);
	}

	// --------------------------------------------------------------------
	toggleShowPhantom() {
		this.showMeterProtocolEquiv = !this.showMeterProtocolEquiv;
		this.saveSettings();
	}

	// --------------------------------------------------------------------
	getPhantomProduct(meterType: string) {
		if (this.phantomSetIds[meterType])
			return 'protocol-set:' + this.phantomSetIds[meterType];
		return '';
	}

	// --------------------------------------------------------------------
	phantomPercent(meterValue: number, protocolValue: number) {
		let percDiff: number = 0;
		if (meterValue === 0 && protocolValue === 0)
			percDiff = 0;
		else if (meterValue === 0 || protocolValue === 0)
			percDiff = 100;
		else
			percDiff = (meterValue - protocolValue) / meterValue * 100;

		if (percDiff !== 0 && percDiff !== 100)
			return percDiff.toFixed(1) + '%';
		else
			return percDiff.toFixed(0) + '%';
	}

	// --------------------------------------------------------------------
	toggleShowAllProtocolMonths() {
		this.protocolShowAllMonths = !this.protocolShowAllMonths;

		if (this.protocolShowAllMonths) {
			this.protocolTimeChunksToShow = this.protocolTimeChunks;
			this.protocolProductsToShow = this.protocolProducts;

		} else {
			if (this.protocolTimeChunks.length > AppConstants.protocolTrafficMonthsToShow) {
				this.protocolTimeChunksToShow = this.protocolTimeChunks.slice(0, AppConstants.protocolTrafficMonthsToShow);
				this.protocolProductsToShow = this.getUniqueProtocolsWithUsage(this.chunkedProtocolUsage, this.protocolTimeChunksToShow, this.protocolProducts);
			} else {
				this.protocolTimeChunksToShow = this.protocolTimeChunks;
				this.protocolProductsToShow = this.protocolProducts;
			} // if
		} // if

		if (this.onlyShowTotals)
			this.protocolProductsVisible = this.getTotalProtocols(this.protocolProductsToShow);
		else
			this.protocolProductsVisible = this.protocolProductsToShow;
		this.buildProtocolInfo();
	}

	// // --------------------------------------------------------------------
	// playSound(fileToPlay: string) {
	// 	let volume: number = 5;
	// 	if (localStorage.getItem(AppConstants.volumeStorageKey)) {
	// 		volume = +localStorage.getItem(AppConstants.volumeStorageKey);
	// 		if (isNaN(volume)) volume = 0;
	// 	} // if

	// 	// this.uiAlertsService.addMsg('Play sound.' + fileToPlay, 'info', '', false, AppConstants.standardMessageAutoCloseTimeSecs);

	// 	PopOverTools.playSound(fileToPlay, volume);
	// }

	// ------------------------------------------------------------------------
	// Back and Forth with components
	// ------------------------------------------------------------------------
	getParentMethod(): any {
		return {
			popupCallBack: async (callBack: string, args: any) => {
				if (callBack === 'disableKey' && args.length === 0)
					this.disableKey();
				else if (callBack === 'XXXXX' && args.length === 1)
					console.log('args=' + args);

				else
					this.uiAlertsService.addMsg('Unknown callBack (' + callBack + ') or bad number of arguments (' + args.length + ').', 'danger', '', false, AppConstants.standardMessageAutoCloseTimeSecs);
			}
		}
	} // getParentMethod

	get typeOfAggregateQueryElement() { return this.aggregateForm.get('typeOfAggregateQuery'); }
}
